import { useState, useEffect } from 'preact/hooks';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n/I18nContext';
import { FeatureLocked } from '../components/FeatureLocked';
import ImportSettings from '../components/settings/ImportSettings';
import BambooHRWizard from '../components/settings/integrations/BambooHRWizard';
import { getLeaveTypes, getLocations, getDepartments, getHolidays, getAllUsers, createDepartment, updateDepartment, deleteDepartment, createLeaveType, updateLeaveType, deleteLeaveType, createLocation, updateLocation, createHoliday, updateHoliday, deleteHoliday, getNotificationSettings, updateNotificationSettings, getSlackChannels, getWorkspaceSettings, updateWorkspaceSettings, getDataStats, downloadBackup, clearNonAdminUsers, clearLeaveHistory, getAllUserBalances, updateUserBalance, initialiseBalances, recalculateBalances, getDepartmentPolicies, updateDepartmentPolicies, getIntegrations, connectIntegration, disconnectIntegration, syncIntegration, getIntegrationLogs, getWorkspaceCompliance, updateWorkspaceCompliance, getCountryComplianceProfiles, getBereavementTiers, updateBereavementTiers, getGoogleDriveBackup, connectGoogleDrive, getGoogleDriveFolders, updateGoogleDriveSettings, runGoogleDriveBackup, disconnectGoogleDrive, restoreFromBackup } from '../lib/api';

export function SettingsPage({ user, workspace }) {
  const { hasFeature } = useAuth();
  const { t, locale } = useI18n();
  // Read tab from URL query param (e.g., /settings?tab=data)
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'workspace';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const [typesData, locsData, deptsData, holidaysData, usersData] = await Promise.all([
        getLeaveTypes(),
        getLocations(),
        getDepartments(),
        getHolidays(new Date().getFullYear()),
        getAllUsers()
      ]);
      setLeaveTypes(typesData || []);
      setLocations(locsData || []);
      setDepartments(deptsData || []);
      setHolidays(holidaysData || []);
      setAllUsers(usersData || []);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return (
      <div class="empty-state" style="margin-top: 3rem;">
        <div class="empty-state-icon">🔒</div>
        <h3>{t('settings.access_denied.title')}</h3>
        <p>{t('settings.access_denied.message')}</p>
      </div>
    );
  }

  const tabs = [
    { id: 'workspace', label: t('settings.tab.workspace'), icon: '⚙️' },
    { id: 'departments', label: t('settings.tab.departments'), icon: '🏢', feature: 'departments' },
    { id: 'locations', label: t('settings.tab.locations'), icon: '📍', feature: 'locations' },
    { id: 'holidays', label: t('settings.tab.holidays'), icon: '🎉', feature: 'public_holidays' },
    { id: 'compliance', label: t('settings.tab.compliance'), icon: '🌍', feature: 'global_compliance' },
    { id: 'balances', label: t('settings.tab.balances'), icon: '📊' },
    { id: 'notifications', label: t('settings.tab.notifications'), icon: '🔔' },
    { id: 'integrations', label: t('settings.tab.integrations'), icon: '🔗' },
    ...(user?.role === 'owner' ? [
      { id: 'import', label: t('settings.tab.import'), icon: '📥' }
    ] : []),
    ...((user?.role === 'owner' || user?.role === 'admin') ? [
      { id: 'data', label: t('settings.tab.data_management'), icon: '🗄️' }
    ] : [])
  ];

  return (
    <div class="settings-page">
      <div class="bento-page-header-row">
        <div class="bento-page-header">
          <h1 class="bento-page-title">{t('settings.page_title')}</h1>
          <p class="bento-page-subtitle">{t('settings.page_subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div class="bento-settings-tabs">
        {tabs.map(tab => {
          const isLocked = tab.feature && !hasFeature(tab.feature);
          return (
            <button
              key={tab.id}
              class={`bento-settings-tab ${activeTab === tab.id ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              title={isLocked ? t('settings.tab.locked_tooltip') : ''}
            >
              <span class="tab-icon">{tab.icon}</span>
              {tab.label}
              {isLocked && (
                <svg class="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div class="loading-screen" style="min-height: 300px;">
          <div class="spinner" />
        </div>
      ) : (
        <>
          {activeTab === 'workspace' && (
            <WorkspaceTab workspace={workspace} departments={departments} locations={locations} leaveTypes={leaveTypes} />
          )}
          {activeTab === 'departments' && (
            hasFeature('departments') ? (
              <DepartmentsTab departments={departments} allUsers={allUsers} leaveTypes={leaveTypes} onRefresh={loadSettings} hasCustomLeaveTypes={hasFeature('custom_leave_types')} />
            ) : (
              <FeatureLocked feature="Departments" description="Organize your team into departments with custom policies and approvers." />
            )
          )}
          {activeTab === 'locations' && (
            hasFeature('locations') ? (
              <LocationsTab locations={locations} onRefresh={loadSettings} />
            ) : (
              <FeatureLocked feature="Locations" description="Support multiple office locations with different timezones and holiday calendars." />
            )
          )}
          {activeTab === 'holidays' && (
            hasFeature('public_holidays') ? (
              <HolidaysTab holidays={holidays} locations={locations} onRefresh={loadSettings} />
            ) : (
              <FeatureLocked feature="Public Holidays" description="Configure public holiday calendars for your locations." />
            )
          )}
          {activeTab === 'compliance' && (
            hasFeature('global_compliance') ? (
              <ComplianceTab leaveTypes={leaveTypes} locations={locations} />
            ) : (
              <FeatureLocked feature="Global Compliance" description="Configure country-specific labor law settings for worldwide compliance." />
            )
          )}
          {activeTab === 'balances' && (
            <OpeningBalancesTab allUsers={allUsers} leaveTypes={leaveTypes} />
          )}
          {activeTab === 'notifications' && (
            <NotificationsTab departments={departments} />
          )}
          {activeTab === 'integrations' && (
            <IntegrationsTab />
          )}
          {activeTab === 'import' && user?.role === 'owner' && (
            <div class="settings-section">
              <ImportSettings onRefresh={loadSettings} />
            </div>
          )}
          {activeTab === 'data' && (user?.role === 'owner' || user?.role === 'admin') && (
            <DataManagementTab />
          )}
        </>
      )}
    </div>
  );
}

function DepartmentsTab({ departments, allUsers, leaveTypes, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_default: false,
    approver_ids: [],
    working_days: [1, 2, 3, 4, 5], // Mon-Fri default
    hours_per_day: 8
  });
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [saving, setSaving] = useState(false);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Leave type management state
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [leaveTypeForm, setLeaveTypeForm] = useState({
    name: '',
    icon: '🏖️',
    color: '#3B82F6',
    unit: 'days',
    requires_approval: true,
    allows_half_day: true,
    is_paid: true,
    resets_annually: true,
    track_per_instance: false,
    priority: 50,
    // Advanced compliance fields
    accrual_method: 'annual',
    accrual_rate: null,
    waiting_period_days: 0,
    accrue_during_probation: true,
    allow_encashment: false,
    max_encashment_days: null,
    payout_on_termination: false,
    max_carryover: null,
    carryover_expiry_months: null,
    documentation_required_after_days: null,
    min_notice_days: 0,
    max_consecutive_days: null,
    minimum_duration: 0,
    use_it_or_lose_it: false
  });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  function openAddLeaveTypeModal() {
    setEditingLeaveType(null);
    setLeaveTypeForm({
      name: '',
      icon: '🏖️',
      color: '#3B82F6',
      unit: 'days',
      requires_approval: true,
      allows_half_day: true,
      is_paid: true,
      resets_annually: true,
      track_per_instance: false,
      priority: 50,
      accrual_method: 'annual',
      accrual_rate: null,
      waiting_period_days: 0,
      accrue_during_probation: true,
      allow_encashment: false,
      max_encashment_days: null,
      payout_on_termination: false,
      max_carryover: null,
      carryover_expiry_months: null,
      documentation_required_after_days: null,
      min_notice_days: 0,
      max_consecutive_days: null,
      minimum_duration: 0,
      use_it_or_lose_it: false
    });
    setShowAdvancedOptions(false);
    setShowLeaveTypeModal(true);
  }

  function openEditLeaveTypeModal(policy) {
    const lt = leaveTypes.find(t => t.id === policy.leave_type_id);
    if (!lt) return;
    setEditingLeaveType(lt);
    setLeaveTypeForm({
      name: lt.name,
      icon: lt.icon || '🏖️',
      color: lt.color || '#3B82F6',
      unit: lt.unit || 'days',
      requires_approval: lt.requires_approval ?? true,
      allows_half_day: lt.allows_half_day ?? true,
      is_paid: lt.is_paid ?? true,
      resets_annually: lt.resets_annually ?? true,
      track_per_instance: lt.track_per_instance ?? false,
      priority: lt.priority ?? 50,
      accrual_method: lt.accrual_method || 'annual',
      accrual_rate: lt.accrual_rate ?? null,
      waiting_period_days: lt.waiting_period_days ?? 0,
      accrue_during_probation: lt.accrue_during_probation ?? true,
      allow_encashment: lt.allow_encashment ?? false,
      max_encashment_days: lt.max_encashment_days ?? null,
      payout_on_termination: lt.payout_on_termination ?? false,
      max_carryover: lt.max_carryover ?? null,
      carryover_expiry_months: lt.carryover_expiry_months ?? null,
      documentation_required_after_days: lt.documentation_required_after_days ?? null,
      min_notice_days: lt.min_notice_days ?? 0,
      max_consecutive_days: lt.max_consecutive_days ?? null,
      minimum_duration: lt.minimum_duration ?? 0,
      use_it_or_lose_it: lt.use_it_or_lose_it ?? false
    });
    setShowAdvancedOptions(false);
    setShowLeaveTypeModal(true);
  }

  async function handleLeaveTypeSubmit(e) {
    e.preventDefault();
    try {
      if (editingLeaveType) {
        await updateLeaveType(editingLeaveType.id, leaveTypeForm);
      } else {
        await createLeaveType({ ...leaveTypeForm, default_entitlement: 25 });
      }
      setShowLeaveTypeModal(false);
      setEditingLeaveType(null);
      onRefresh();
    } catch (err) {
      alert(`Failed to ${editingLeaveType ? 'update' : 'create'} leave type`);
    }
  }

  async function handleDeleteLeaveType(policy) {
    const lt = leaveTypes.find(t => t.id === policy.leave_type_id);
    if (!lt) return;
    if (!confirm(`Delete "${lt.name}"? This will remove it from all departments.`)) return;
    try {
      await deleteLeaveType(lt.id);
      onRefresh();
    } catch (err) {
      alert('Failed to delete. It may be in use by leave requests.');
    }
  }

  function getDepartmentMembers(deptId) {
    return allUsers.filter(u => u.department_id === deptId && u.is_active);
  }

  function getDepartmentApprovers(dept) {
    // Get approvers from the stored approver_ids array
    const approverIds = dept.approver_ids || [];
    return allUsers.filter(u => approverIds.includes(u.id));
  }

  async function openEditModal(dept) {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      is_default: dept.is_default || false,
      approver_ids: dept.approver_ids || [],
      working_days: dept.working_days || [1, 2, 3, 4, 5],
      hours_per_day: dept.hours_per_day ?? 8
    });
    setShowModal(true);
    // Load policies for this department
    setLoadingPolicies(true);
    try {
      const deptPolicies = await getDepartmentPolicies(dept.id);
      setPolicies(deptPolicies || []);
    } catch (err) {
      console.error('Failed to load department policies:', err);
      // Initialize with leave types defaults
      setPolicies((leaveTypes || []).map(lt => ({
        leave_type_id: lt.id,
        name: lt.name,
        icon: lt.icon,
        color: lt.color,
        unit: lt.unit,
        default_entitlement: lt.default_entitlement,
        entitlement: lt.default_entitlement,
        is_enabled: true,
        has_custom_policy: false
      })));
    } finally {
      setLoadingPolicies(false);
    }
  }

  function openAddModal() {
    setEditingDept(null);
    setFormData({
      name: '',
      is_default: false,
      approver_ids: [],
      working_days: [1, 2, 3, 4, 5],
      hours_per_day: 8
    });
    // Initialize policies with leave type defaults
    setPolicies((leaveTypes || []).map(lt => ({
      leave_type_id: lt.id,
      name: lt.name,
      icon: lt.icon,
      color: lt.color,
      unit: lt.unit,
      default_entitlement: lt.default_entitlement,
      entitlement: lt.default_entitlement,
      is_enabled: true,
      has_custom_policy: false
    })));
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let deptId;
      if (editingDept) {
        await updateDepartment(editingDept.id, formData);
        deptId = editingDept.id;
      } else {
        const result = await createDepartment(formData);
        deptId = result.id;
      }
      // Save leave policies
      if (deptId && policies.length > 0) {
        await updateDepartmentPolicies(deptId, policies.map(p => ({
          leave_type_id: p.leave_type_id,
          entitlement: p.entitlement,
          is_enabled: p.is_enabled
        })));

        // Update leave type units if changed
        for (const policy of policies) {
          const originalLeaveType = leaveTypes.find(lt => lt.id === policy.leave_type_id);
          if (originalLeaveType && originalLeaveType.unit !== policy.unit) {
            await updateLeaveType(policy.leave_type_id, { unit: policy.unit });
          }
        }
      }
      setShowModal(false);
      onRefresh();
    } catch (err) {
      alert('Failed to save department');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(deptId) {
    if (!confirm('Are you sure you want to delete this department? Users will be unassigned.')) return;
    try {
      await deleteDepartment(deptId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete department');
    }
  }

  async function handleSetDefault(deptId) {
    try {
      await updateDepartment(deptId, { is_default: true });
      onRefresh();
    } catch (err) {
      alert('Failed to set default department');
    }
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Departments</h2>
          <p>Configure departments with their leave types, entitlements, and approvers</p>
        </div>
        <button class="btn btn-primary" onClick={openAddModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Department
        </button>
      </div>

      <div class="departments-grid">
        {departments.length > 0 ? (
          departments.map(dept => {
            const members = getDepartmentMembers(dept.id);
            const approvers = getDepartmentApprovers(dept);
            const displayMembers = members.slice(0, 5);
            const moreCount = members.length - 5;

            return (
              <div key={dept.id} class={`department-card ${dept.is_default ? 'default' : ''}`}>
                <div class="department-card-header">
                  <div class="department-icon">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div class="department-title">
                    <h3>{dept.name}</h3>
                    {!!dept.is_default && <span class="badge badge-approved">Default</span>}
                  </div>
                  <div class="department-menu">
                    <button class="btn-icon" onClick={() => openEditModal(dept)}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="department-stats">
                  <div class="stat-item">
                    <span class="stat-value">{members.length}</span>
                    <span class="stat-label">Members</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-value">{approvers.length}</span>
                    <span class="stat-label">Approvers</span>
                  </div>
                </div>

                {members.length > 0 && (
                  <div class="department-members">
                    <span class="members-label">Team Members</span>
                    <div class="avatar-stack">
                      {displayMembers.map(member => (
                        <div key={member.id} class="avatar avatar-sm" title={member.name}>
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt="" />
                          ) : (
                            member.name?.charAt(0)
                          )}
                        </div>
                      ))}
                      {moreCount > 0 && (
                        <div class="avatar avatar-sm more-count">+{moreCount}</div>
                      )}
                    </div>
                  </div>
                )}

                {approvers.length > 0 && (
                  <div class="department-approvers">
                    <span class="approvers-label">Approvers</span>
                    <div class="approvers-list">
                      {approvers.map(approver => (
                        <div key={approver.id} class="approver-chip">
                          <div class="avatar avatar-xs">
                            {approver.avatar_url ? (
                              <img src={approver.avatar_url} alt="" />
                            ) : (
                              approver.name?.charAt(0)
                            )}
                          </div>
                          <span>{approver.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div class="department-actions">
                  <button class="btn btn-sm btn-primary" onClick={() => openEditModal(dept)}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="mr-1">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Leave Policies
                  </button>
                  {!dept.is_default && (
                    <button class="btn btn-sm btn-secondary" onClick={() => handleSetDefault(dept.id)}>
                      Set as Default
                    </button>
                  )}
                  <button class="btn btn-sm btn-danger-outline" onClick={() => handleDelete(dept.id)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div class="empty-card full-width">
            <div class="empty-state-icon">🏢</div>
            <h3>No departments yet</h3>
            <p>Create departments to organize your team</p>
            <button class="btn btn-primary" onClick={openAddModal}>Add First Department</button>
          </div>
        )}
      </div>

      {/* Department Modal */}
      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal modal-scrollable" role="dialog" aria-modal="true">
            <form onSubmit={handleSubmit} class="flex-col max-h-screen-90 overflow-hidden">
              <div class="modal-header flex-shrink-0">
                <h2>{editingDept ? 'Edit Department' : 'Add Department'}</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)} aria-label="Close modal">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body modal-body-scroll">
                <div class="form-group">
                  <label>Department Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Engineering, Marketing"
                  />
                </div>

                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    Set as Default Department
                  </label>
                  <p class="form-hint">New users will be automatically assigned to this department</p>
                </div>

                {/* Working Days Configuration */}
                <div class="form-group section-divider">
                  <label class="font-semibold mb-2 block">Working Days</label>
                  <p class="form-hint mb-3">Select which days of the week are working days for this department</p>
                  <div class="flex gap-2 flex-wrap">
                    {dayNames.map((day, idx) => (
                      <label
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '48px',
                          height: '40px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          border: '2px solid',
                          borderColor: formData.working_days.includes(idx) ? '#3b82f6' : '#e5e7eb',
                          background: formData.working_days.includes(idx) ? '#eff6ff' : 'white',
                          color: formData.working_days.includes(idx) ? '#1e40af' : '#6b7280',
                          transition: 'all 0.15s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.working_days.includes(idx)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, working_days: [...formData.working_days, idx].sort() });
                            } else {
                              setFormData({ ...formData, working_days: formData.working_days.filter(d => d !== idx) });
                            }
                          }}
                          class="hidden"
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Hours per Day */}
                <div class="form-group">
                  <label>Hours per Working Day</label>
                  <p class="form-hint">Used for converting between hours and days</p>
                  <div class="flex items-center gap-2">
                    <input
                      type="number"
                      class="form-input w-input-sm"
                      value={formData.hours_per_day}
                      onChange={(e) => setFormData({ ...formData, hours_per_day: parseFloat(e.target.value) || 8 })}
                      min="1"
                      max="24"
                      step="0.5"
                    />
                    <span class="text-secondary text-sm">hours</span>
                  </div>
                </div>

                <div class="form-group section-divider">
                  <label>Approvers</label>
                  <p class="form-hint">Select users who can approve leave requests for this department</p>
                  <div class="approver-selector">
                    {allUsers.filter(u => u.is_active && (u.role === 'approver' || u.role === 'admin' || u.role === 'owner')).map(u => (
                      <label key={u.id} class="approver-option">
                        <input
                          type="checkbox"
                          checked={formData.approver_ids.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, approver_ids: [...formData.approver_ids, u.id] });
                            } else {
                              setFormData({ ...formData, approver_ids: formData.approver_ids.filter(id => id !== u.id) });
                            }
                          }}
                        />
                        <div class="avatar avatar-sm">
                          {u.avatar_url ? <img src={u.avatar_url} alt="" /> : u.name?.charAt(0)}
                        </div>
                        <span>{u.name}</span>
                        <span class="role-badge">{u.role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Leave Policies Section */}
                <div class="form-group" style={{
                  marginTop: '1.5rem',
                  paddingTop: '1.5rem',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <div class="flex items-center gap-2">
                      <svg width="20" height="20" fill="none" stroke="#3b82f6" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <label style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e40af' }}>Leave Types & Entitlements</label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-secondary text-xs"
                      onClick={openAddLeaveTypeModal}
                    >
                      + Add Leave Type
                    </button>
                  </div>
                  <p class="form-hint mb-4">
                    Configure leave types and set entitlements for this department.
                  </p>
                  {loadingPolicies ? (
                    <div style="padding: 1rem; text-align: center;">
                      <div class="spinner" style="width: 20px; height: 20px;" />
                    </div>
                  ) : policies.length > 0 ? (
                    <div class="leave-policies-list-v2">
                      {policies.map((policy, idx) => (
                        <div key={policy.leave_type_id} class={`leave-policy-item ${!policy.is_enabled ? 'disabled' : ''}`}>
                          <div class="policy-row-main">
                            <input
                              type="checkbox"
                              class="policy-checkbox"
                              checked={policy.is_enabled}
                              onChange={(e) => {
                                const newPolicies = [...policies];
                                newPolicies[idx] = { ...policy, is_enabled: e.target.checked };
                                setPolicies(newPolicies);
                              }}
                            />
                            <span class="policy-icon">{policy.icon}</span>
                            <span class="policy-name">{policy.name}</span>
                            <div class="policy-actions">
                              <button
                                type="button"
                                class="btn-icon-sm"
                                title="Edit leave type"
                                onClick={() => openEditLeaveTypeModal(policy)}
                              >
                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                class="btn-icon-sm btn-icon-danger"
                                title="Delete leave type"
                                onClick={() => handleDeleteLeaveType(policy)}
                              >
                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div class="policy-row-entitlement">
                            <label class="unlimited-label">
                              <input
                                type="checkbox"
                                checked={policy.entitlement === -1}
                                disabled={!policy.is_enabled}
                                onChange={(e) => {
                                  const newPolicies = [...policies];
                                  newPolicies[idx] = { ...policy, entitlement: e.target.checked ? -1 : policy.default_entitlement || 25 };
                                  setPolicies(newPolicies);
                                }}
                              />
                              Unlimited
                            </label>
                            {policy.entitlement !== -1 && (
                              <>
                                <input
                                  type="number"
                                  class="entitlement-input"
                                  min="0"
                                  step="0.5"
                                  value={policy.entitlement}
                                  disabled={!policy.is_enabled}
                                  onChange={(e) => {
                                    const newPolicies = [...policies];
                                    newPolicies[idx] = { ...policy, entitlement: parseFloat(e.target.value) || 0 };
                                    setPolicies(newPolicies);
                                  }}
                                />
                                <span class="entitlement-display">
                                  days
                                  <span class="entitlement-hours">
                                    ({policy.entitlement * (formData.hours_per_day || 8)} hours)
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: '#f8fafc',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No leave types configured yet</p>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        onClick={openAddLeaveTypeModal}
                      >
                        Add First Leave Type
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div class="modal-footer flex-shrink-0 bg-white">
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingDept ? 'Save Changes' : 'Create Department')}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Leave Type Modal */}
      {showLeaveTypeModal && (
        <>
          <div class="modal-overlay" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }} style={{ zIndex: 1001 }} />
          <div class="modal" style={{ zIndex: 1002, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleLeaveTypeSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
              <div class="modal-header" style={{ flexShrink: 0 }}>
                <h2>{editingLeaveType ? 'Edit' : 'Add'} Leave Type</h2>
                <button type="button" class="btn-icon" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body modal-body-scroll">
                <div class="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={leaveTypeForm.name}
                    onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })}
                    required
                    placeholder="e.g., Vacation, Sick Leave"
                  />
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Icon</label>
                    <input
                      type="text"
                      class="form-input"
                      value={leaveTypeForm.icon}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, icon: e.target.value })}
                      placeholder="🏖️"
                    />
                  </div>
                  <div class="form-group" style={{ flex: 2 }}>
                    <label>Colour</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                      <input
                        type="color"
                        class="form-color-picker"
                        value={leaveTypeForm.color}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, color: e.target.value })}
                        style={{
                          width: '50px',
                          height: '40px',
                          padding: '2px',
                          border: '2px solid #E5E7EB',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      />
                      <div class="flex gap-2 flex-wrap">
                        {['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#84CC16'].map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setLeaveTypeForm({ ...leaveTypeForm, color })}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              border: leaveTypeForm.color === color ? '2px solid #374151' : '2px solid transparent',
                              backgroundColor: color,
                              cursor: 'pointer',
                              boxShadow: leaveTypeForm.color === color ? '0 0 0 2px white, 0 0 0 4px ' + color : 'none'
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label>Track By</label>
                  <div class="flex gap-4 mt-2 flex-wrap">
                    <label class="toggle-label">
                      <input
                        type="radio"
                        name="unit"
                        value="hours"
                        checked={leaveTypeForm.unit === 'hours'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'hours' })}
                      />
                      <span>Hours</span>
                    </label>
                    <label class="toggle-label">
                      <input
                        type="radio"
                        name="unit"
                        value="days"
                        checked={leaveTypeForm.unit === 'days'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'days' })}
                      />
                      <span>Days</span>
                    </label>
                    <label class="toggle-label">
                      <input
                        type="radio"
                        name="unit"
                        value="weeks"
                        checked={leaveTypeForm.unit === 'weeks'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'weeks' })}
                      />
                      <span>Weeks</span>
                    </label>
                    <label class="toggle-label">
                      <input
                        type="radio"
                        name="unit"
                        value="months"
                        checked={leaveTypeForm.unit === 'months'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'months' })}
                      />
                      <span>Months</span>
                    </label>
                  </div>
                  <p class="form-hint">Choose the unit for tracking this leave type</p>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.requires_approval}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, requires_approval: e.target.checked })}
                    />
                    Requires Approval
                  </label>
                </div>
                {leaveTypeForm.unit === 'days' && (
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.allows_half_day}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, allows_half_day: e.target.checked })}
                      />
                      Allows Half Day
                    </label>
                  </div>
                )}
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.is_paid}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, is_paid: e.target.checked })}
                    />
                    Paid Leave
                  </label>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.track_per_instance}
                      onChange={(e) => setLeaveTypeForm({
                        ...leaveTypeForm,
                        track_per_instance: e.target.checked,
                        // When per-instance is enabled, resets_annually is irrelevant
                        resets_annually: e.target.checked ? true : leaveTypeForm.resets_annually
                      })}
                    />
                    Track Per Instance
                  </label>
                  <small class="text-secondary mt-1 block">
                    For leave types tied to events (e.g., maternity per pregnancy, paternity per birth).
                    An admin must create an instance before employees can request this leave.
                  </small>
                </div>
                {!leaveTypeForm.track_per_instance && (
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.resets_annually}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, resets_annually: e.target.checked })}
                      />
                      Resets Annually
                    </label>
                    <small class="text-secondary mt-1 block">
                      Uncheck for lifetime entitlements that don't reset each year
                    </small>
                  </div>
                )}

                {/* Priority Level */}
                <div class="form-group">
                  <label>Priority Level</label>
                  <select
                    value={leaveTypeForm.priority}
                    onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, priority: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                  >
                    <option value={100}>100 - Highest (Sick Leave)</option>
                    <option value={95}>95 - Very High (Bereavement)</option>
                    <option value={90}>90 - High (Maternity/Paternity)</option>
                    <option value={85}>85 - Medium-High (Parental)</option>
                    <option value={80}>80 - Medium (Marriage)</option>
                    <option value={50}>50 - Standard (Vacation)</option>
                    <option value={40}>40 - Low (Personal Day)</option>
                    <option value={30}>30 - Lowest (TOIL)</option>
                  </select>
                  <small class="text-secondary mt-1 block">
                    Higher priority leave will automatically override lower priority leave on overlapping days
                  </small>
                </div>

                {/* Advanced Options Toggle */}
                <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '1rem', marginTop: '1rem' }}>
                  <button
                    type="button"
                    class="btn btn-link"
                    style={{ padding: 0, color: '#6B7280', fontSize: '0.875rem' }}
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  >
                    {showAdvancedOptions ? '▼' : '▶'} Advanced Compliance Options
                  </button>
                </div>

                {showAdvancedOptions && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#F9FAFB', borderRadius: '8px' }}>
                    {/* Accrual Method */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Accrual Method</label>
                      <select
                        value={leaveTypeForm.accrual_method}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, accrual_method: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                      >
                        <option value="annual">Annual (full entitlement at year start)</option>
                        <option value="monthly">Monthly (1/12 per month)</option>
                        <option value="per_days_worked">Per Days Worked (India-style)</option>
                        <option value="per_hours_worked">Per Hours Worked</option>
                        <option value="anniversary">Anniversary (based on hire date)</option>
                      </select>
                      <small class="text-secondary">How leave is earned throughout the year</small>
                    </div>

                    {/* Accrual Rate (shown for per_days_worked or per_hours_worked) */}
                    {(leaveTypeForm.accrual_method === 'per_days_worked' || leaveTypeForm.accrual_method === 'per_hours_worked') && (
                      <div class="form-group" style={{ marginBottom: '1rem' }}>
                        <label>Accrual Rate</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>1 day per</span>
                          <input
                            type="number"
                            value={leaveTypeForm.accrual_rate || 20}
                            onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, accrual_rate: parseInt(e.target.value) })}
                            style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                            min="1"
                          />
                          <span>{leaveTypeForm.accrual_method === 'per_hours_worked' ? 'hours worked' : 'days worked'}</span>
                        </div>
                        <small class="text-secondary">E.g., India: 1 day per 20 days worked</small>
                      </div>
                    )}

                    {/* Waiting Period */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Waiting Period</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          value={leaveTypeForm.waiting_period_days || 0}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, waiting_period_days: parseInt(e.target.value) })}
                          style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                          min="0"
                        />
                        <span>days before leave can be used</span>
                      </div>
                      <small class="text-secondary">Eligibility waiting period for new employees</small>
                    </div>

                    {/* Accrue During Probation */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={leaveTypeForm.accrue_during_probation}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, accrue_during_probation: e.target.checked })}
                        />
                        Accrue during probation
                      </label>
                      <small class="text-secondary">Whether leave accrues before probation ends</small>
                    </div>

                    {/* Documentation Required */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Documentation Required After</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          value={leaveTypeForm.documentation_required_after_days || ''}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, documentation_required_after_days: e.target.value ? parseInt(e.target.value) : null })}
                          style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                          min="1"
                          placeholder="—"
                        />
                        <span>consecutive days (leave blank for never)</span>
                      </div>
                      <small class="text-secondary">E.g., sick note required after 3 days</small>
                    </div>

                    {/* Minimum Notice */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Minimum Notice Required</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          value={leaveTypeForm.min_notice_days || 0}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, min_notice_days: parseInt(e.target.value) })}
                          style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                          min="0"
                        />
                        <span>days advance notice</span>
                      </div>
                      <small class="text-secondary">Minimum days before leave starts (0 = no requirement)</small>
                    </div>

                    {/* Max Consecutive Days */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Maximum Consecutive Days</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          value={leaveTypeForm.max_consecutive_days || ''}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, max_consecutive_days: e.target.value ? parseInt(e.target.value) : null })}
                          style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                          min="1"
                          placeholder="—"
                        />
                        <span>days (leave blank for unlimited)</span>
                      </div>
                      <small class="text-secondary">Maximum days that can be taken consecutively</small>
                    </div>

                    {/* Minimum Duration */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label>Minimum Duration</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          value={leaveTypeForm.minimum_duration || 0}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, minimum_duration: parseFloat(e.target.value) || 0 })}
                          style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                          min="0"
                          step="0.5"
                        />
                        <span>{leaveTypeForm.unit === 'hours' ? 'hours' : 'days'} minimum</span>
                      </div>
                      <small class="text-secondary">Minimum duration for a single leave request (0 = no minimum)</small>
                    </div>

                    <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

                    {/* Carryover Settings */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={leaveTypeForm.use_it_or_lose_it}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, use_it_or_lose_it: e.target.checked })}
                        />
                        Use-it-or-lose-it (no carryover)
                      </label>
                      <small class="text-secondary">Unused leave expires at year end</small>
                    </div>

                    {!leaveTypeForm.use_it_or_lose_it && (
                      <>
                        <div class="form-group" style={{ marginBottom: '1rem' }}>
                          <label>Maximum Carryover</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              value={leaveTypeForm.max_carryover || ''}
                              onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, max_carryover: e.target.value ? parseFloat(e.target.value) : null })}
                              style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                              min="0"
                              step="0.5"
                              placeholder="—"
                            />
                            <span>days (leave blank for unlimited)</span>
                          </div>
                        </div>

                        <div class="form-group" style={{ marginBottom: '1rem' }}>
                          <label>Carryover Expiry</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              value={leaveTypeForm.carryover_expiry_months || ''}
                              onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, carryover_expiry_months: e.target.value ? parseInt(e.target.value) : null })}
                              style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                              min="1"
                              placeholder="—"
                            />
                            <span>months (leave blank for never expires)</span>
                          </div>
                          <small class="text-secondary">E.g., UK carryover expires after 18 months</small>
                        </div>
                      </>
                    )}

                    <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

                    {/* Encashment/Payout */}
                    <div class="form-group" style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={leaveTypeForm.allow_encashment}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, allow_encashment: e.target.checked })}
                        />
                        Allow encashment (cash out unused leave)
                      </label>
                      <small class="text-secondary">Employees can request payment for unused days</small>
                    </div>

                    {leaveTypeForm.allow_encashment && (
                      <div class="form-group" style={{ marginBottom: '1rem' }}>
                        <label>Maximum Encashment</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="number"
                            value={leaveTypeForm.max_encashment_days || ''}
                            onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, max_encashment_days: e.target.value ? parseFloat(e.target.value) : null })}
                            style={{ width: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                            min="0"
                            step="0.5"
                            placeholder="—"
                          />
                          <span>days per year (leave blank for unlimited)</span>
                        </div>
                      </div>
                    )}

                    <div class="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={leaveTypeForm.payout_on_termination}
                          onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, payout_on_termination: e.target.checked })}
                        />
                        Payout on termination
                      </label>
                      <small class="text-secondary">Unused balance must be paid out when employee leaves</small>
                    </div>
                  </div>
                )}
              </div>
              <div class="modal-footer flex-shrink-0 bg-white">
                <button type="button" class="btn btn-secondary" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }}>Cancel</button>
                <button type="submit" class="btn btn-primary">{editingLeaveType ? 'Update' : 'Create'} Leave Type</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// Comprehensive timezone list
const TIMEZONES = [
  // Europe
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin (GMT/IST)' },
  { value: 'Europe/Lisbon', label: 'Europe/Lisbon (WET/WEST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/Malta', label: 'Europe/Malta (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (CET/CEST)' },
  { value: 'Europe/Prague', label: 'Europe/Prague (CET/CEST)' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)' },
  { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (CET/CEST)' },
  { value: 'Europe/Oslo', label: 'Europe/Oslo (CET/CEST)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET/EEST)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (EET/EEST)' },
  { value: 'Europe/Bucharest', label: 'Europe/Bucharest (EET/EEST)' },
  { value: 'Europe/Sofia', label: 'Europe/Sofia (EET/EEST)' },
  { value: 'Europe/Istanbul', label: 'Europe/Istanbul (TRT)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  // Americas
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Toronto', label: 'America/Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (CST)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (ART)' },
  // Asia
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT)' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB)' },
  { value: 'Asia/Manila', label: 'Asia/Manila (PHT)' },
  // Oceania
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
  // Africa
  { value: 'Africa/Cairo', label: 'Africa/Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT)' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT)' },
  // UTC
  { value: 'UTC', label: 'UTC' },
];

function LocationsTab({ locations, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    country_code: '',
    city: '',
    timezone: 'Europe/Malta',
    is_default: false
  });

  function openCreateModal() {
    setEditingLocation(null);
    setFormData({ name: '', country_code: '', city: '', timezone: 'Europe/Malta', is_default: false });
    setShowModal(true);
  }

  function openEditModal(loc) {
    setEditingLocation(loc);
    setFormData({
      name: loc.name || '',
      country_code: loc.country_code || '',
      city: loc.city || '',
      timezone: loc.timezone || 'Europe/Malta',
      is_default: !!loc.is_default
    });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingLocation) {
        await updateLocation(editingLocation.id, formData);
      } else {
        await createLocation(formData);
      }
      setShowModal(false);
      setEditingLocation(null);
      setFormData({ name: '', country_code: '', city: '', timezone: 'Europe/Malta', is_default: false });
      onRefresh();
    } catch (err) {
      alert(editingLocation ? 'Failed to update location' : 'Failed to create location');
    }
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Locations</h2>
          <p>Configure office locations with their timezones and holidays</p>
        </div>
        <button class="btn btn-primary" onClick={openCreateModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Location
        </button>
      </div>

      <div class="settings-grid">
        {locations.length > 0 ? (
          locations.map(loc => (
            <div key={loc.id} class={`settings-card ${loc.is_default ? 'default' : ''}`} onClick={() => openEditModal(loc)} style={{ cursor: 'pointer' }}>
              <div class="settings-card-header">
                <div class="location-icon">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div class="settings-card-title">
                  <h3>{loc.name}</h3>
                  {loc.is_default && <span class="badge badge-approved">Default</span>}
                </div>
              </div>
              <div class="settings-card-body">
                <div class="detail-row">
                  <span class="detail-label">Country</span>
                  <span class="detail-value">{loc.country_code || 'Not set'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">City</span>
                  <span class="detail-value">{loc.city || 'Not set'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Timezone</span>
                  <span class="detail-value">{loc.timezone}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Members</span>
                  <span class="detail-value">{loc.user_count || 0} users</span>
                </div>
              </div>
              <div class="settings-card-footer" style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border-color)', textAlign: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Click to edit</span>
              </div>
            </div>
          ))
        ) : (
          <div class="empty-card">
            <div class="empty-state-icon">📍</div>
            <p>No locations configured yet</p>
          </div>
        )}
      </div>

      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal modal-scrollable" role="dialog" aria-modal="true">
            <form onSubmit={handleSubmit} class="flex-col max-h-screen-90 overflow-hidden">
              <div class="modal-header flex-shrink-0">
                <h2>{editingLocation ? 'Edit Location' : 'Add Location'}</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)} aria-label="Close modal">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body modal-body-scroll">
                <div class="form-group">
                  <label>Location Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Malta Office, London HQ"
                  />
                </div>
                <div class="form-group">
                  <label>Country Code</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., MT, GB, US"
                    maxLength="2"
                  />
                </div>
                <div class="form-group">
                  <label>City (for weather forecasts)</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Valletta, London, Warsaw"
                  />
                  <span class="form-hint">Used for daily weather forecasts. Leave blank to skip weather for this location.</span>
                </div>
                <div class="form-group">
                  <label>Timezone</label>
                  <select
                    class="form-select"
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    Set as Default Location
                  </label>
                </div>
              </div>
              <div class="modal-footer flex-shrink-0 bg-white">
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary">{editingLocation ? 'Save Changes' : 'Create Location'}</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function ComplianceTab({ leaveTypes, locations }) {
  const [compliance, setCompliance] = useState(null);
  const [countryProfiles, setCountryProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('workspace');

  useEffect(() => {
    loadComplianceData();
  }, []);

  async function loadComplianceData() {
    try {
      const [complianceData, countriesData] = await Promise.all([
        getWorkspaceCompliance(),
        getCountryComplianceProfiles()
      ]);
      setCompliance(complianceData || {});
      setCountryProfiles(countriesData || []);
    } catch (err) {
      console.error('Failed to load compliance data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateWorkspaceCompliance(compliance);
      alert('Compliance settings saved successfully');
    } catch (err) {
      alert('Failed to save compliance settings');
    } finally {
      setSaving(false);
    }
  }

  function updateField(field, value) {
    setCompliance(prev => ({ ...prev, [field]: value }));
  }

  if (loading) {
    return <div class="settings-section"><p>Loading compliance settings...</p></div>;
  }

  return (
    <div class="compliance-settings">
      {/* Section Navigation */}
      <div class="compliance-nav" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {[
          { id: 'workspace', label: 'Workspace Settings' },
          { id: 'ssp', label: 'UK SSP' },
          { id: 'toil', label: 'TOIL/Overtime' },
          { id: 'countries', label: 'Country Reference' }
        ].map(section => (
          <button
            key={section.id}
            type="button"
            class={`btn btn-sm ${activeSection === section.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Workspace Settings Section */}
      {activeSection === 'workspace' && (
        <div class="settings-section">
          <h3>General Compliance Settings</h3>
          <p class="text-muted" style={{ marginBottom: '1rem' }}>Configure how leave is calculated based on your country's labor laws.</p>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Leave Year Start</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                class="form-control"
                style={{ width: 'auto' }}
                value={compliance.leave_year_start_month || 1}
                onChange={e => updateField('leave_year_start_month', parseInt(e.target.value))}
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                class="form-control"
                style={{ width: 'auto' }}
                value={compliance.leave_year_start_day || 1}
                onChange={e => updateField('leave_year_start_day', parseInt(e.target.value))}
              >
                {Array.from({ length: 28 }, (_, i) => (
                  <option key={i} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>
            <small class="text-muted">When the leave year begins (e.g., April 6 for UK tax year)</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Default Probation Period</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                class="form-control"
                style={{ width: '100px' }}
                value={compliance.default_probation_days || 90}
                onChange={e => updateField('default_probation_days', parseInt(e.target.value))}
                min="0"
                max="365"
              />
              <span>days</span>
            </div>
            <small class="text-muted">Standard probation period for new employees</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Part-Time Calculation Method</label>
            <select
              class="form-control"
              value={compliance.part_time_calculation || 'pro_rata'}
              onChange={e => updateField('part_time_calculation', e.target.value)}
            >
              <option value="pro_rata">Pro-rata (based on hours worked)</option>
              <option value="full">Full entitlement (same as full-time)</option>
              <option value="days_per_week">Pro-rata (based on days per week)</option>
            </select>
            <small class="text-muted">How to calculate entitlements for part-time employees</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Shift Worker Extra Days</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                class="form-control"
                style={{ width: '100px' }}
                value={compliance.shift_worker_extra_days || 0}
                onChange={e => updateField('shift_worker_extra_days', parseFloat(e.target.value))}
                min="0"
                step="0.5"
              />
              <span>days</span>
            </div>
            <small class="text-muted">Additional statutory leave for shift workers (UK: 1.6 days)</small>
          </div>

          <button type="button" class="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Workspace Settings'}
          </button>
        </div>
      )}

      {/* UK Statutory Sick Pay Section */}
      {activeSection === 'ssp' && (
        <div class="settings-section">
          <h3>UK Statutory Sick Pay (SSP)</h3>
          <p class="text-muted" style={{ marginBottom: '1rem' }}>Configure SSP tracking for UK-based employees.</p>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={compliance.ssp_enabled || false}
                onChange={e => updateField('ssp_enabled', e.target.checked)}
              />
              Enable SSP Tracking
            </label>
            <small class="text-muted">Track statutory sick pay eligibility and periods</small>
          </div>

          {compliance.ssp_enabled && (
            <>
              <div class="form-group" style={{ marginBottom: '1rem' }}>
                <label>SSP Weekly Rate</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>£</span>
                  <input
                    type="number"
                    class="form-control"
                    style={{ width: '120px' }}
                    value={compliance.ssp_rate || 116.75}
                    onChange={e => updateField('ssp_rate', parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                  <span>/week</span>
                </div>
                <small class="text-muted">Current UK SSP rate (2024-25: £116.75/week)</small>
              </div>

              <div class="form-group" style={{ marginBottom: '1rem' }}>
                <label>Waiting Days</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    class="form-control"
                    style={{ width: '80px' }}
                    value={compliance.ssp_waiting_days || 3}
                    onChange={e => updateField('ssp_waiting_days', parseInt(e.target.value))}
                    min="0"
                    max="7"
                  />
                  <span>qualifying days</span>
                </div>
                <small class="text-muted">Number of waiting days before SSP is payable</small>
              </div>

              <div class="form-group" style={{ marginBottom: '1rem' }}>
                <label>Maximum SSP Duration</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    class="form-control"
                    style={{ width: '80px' }}
                    value={compliance.ssp_max_weeks || 28}
                    onChange={e => updateField('ssp_max_weeks', parseInt(e.target.value))}
                    min="1"
                    max="52"
                  />
                  <span>weeks</span>
                </div>
                <small class="text-muted">Maximum weeks SSP is payable per period of incapacity</small>
              </div>

              <div class="form-group" style={{ marginBottom: '1rem' }}>
                <label>Minimum Earnings Threshold (LEL)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>£</span>
                  <input
                    type="number"
                    class="form-control"
                    style={{ width: '120px' }}
                    value={compliance.ssp_min_earnings_threshold || 123}
                    onChange={e => updateField('ssp_min_earnings_threshold', parseFloat(e.target.value))}
                    step="1"
                    min="0"
                  />
                  <span>/week</span>
                </div>
                <small class="text-muted">Lower Earnings Limit - employees below this don't qualify</small>
              </div>
            </>
          )}

          <button type="button" class="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save SSP Settings'}
          </button>
        </div>
      )}

      {/* TOIL/Overtime Section */}
      {activeSection === 'toil' && (
        <div class="settings-section">
          <h3>Time Off In Lieu (TOIL)</h3>
          <p class="text-muted" style={{ marginBottom: '1rem' }}>Configure overtime and TOIL tracking rules.</p>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={compliance.toil_exempt_only || false}
                onChange={e => updateField('toil_exempt_only', e.target.checked)}
              />
              Restrict TOIL to Exempt Employees Only
            </label>
            <small class="text-muted">US FLSA: Non-exempt employees must be paid overtime, not given TOIL</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Maximum TOIL Balance</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                class="form-control"
                style={{ width: '100px' }}
                value={compliance.toil_max_hours || 240}
                onChange={e => updateField('toil_max_hours', parseFloat(e.target.value))}
                min="0"
                step="1"
              />
              <span>hours</span>
            </div>
            <small class="text-muted">Maximum hours that can be accrued (public sector often 240h)</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>TOIL Expiry</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                class="form-control"
                style={{ width: '80px' }}
                value={compliance.toil_expiry_pay_periods || 26}
                onChange={e => updateField('toil_expiry_pay_periods', parseInt(e.target.value))}
                min="0"
              />
              <span>pay periods</span>
            </div>
            <small class="text-muted">When unused TOIL expires (0 = never expires)</small>
          </div>

          <div class="form-group" style={{ marginBottom: '1rem' }}>
            <label>Default Accrual Rate</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="number"
                class="form-control"
                style={{ width: '80px' }}
                value={compliance.toil_default_rate || 1.0}
                onChange={e => updateField('toil_default_rate', parseFloat(e.target.value))}
                min="1"
                max="2"
                step="0.5"
              />
              <span>x (1.0 = hour-for-hour, 1.5 = time-and-a-half)</span>
            </div>
            <small class="text-muted">How much TOIL is earned per hour worked</small>
          </div>

          <button type="button" class="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save TOIL Settings'}
          </button>
        </div>
      )}

      {/* Country Reference Section */}
      {activeSection === 'countries' && (
        <div class="settings-section">
          <h3>Country Compliance Reference</h3>
          <p class="text-muted" style={{ marginBottom: '1rem' }}>Reference guide for labor law requirements by country.</p>

          {countryProfiles.length === 0 ? (
            <p>No country profiles available.</p>
          ) : (
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Statutory Annual</th>
                    <th>Sick Leave</th>
                    <th>Maternity</th>
                    <th>Paternity</th>
                    <th>Sick Note After</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {countryProfiles.map(profile => (
                    <tr key={profile.country_code}>
                      <td>
                        <strong>{profile.country_code}</strong>
                        <br />
                        <small class="text-muted">{profile.country_name}</small>
                      </td>
                      <td>{profile.statutory_annual_leave_days ?? '—'} days</td>
                      <td>{profile.statutory_sick_leave_days ?? '—'} days</td>
                      <td>{profile.statutory_maternity_weeks ?? '—'} wks</td>
                      <td>{profile.statutory_paternity_weeks ?? '—'} wks</td>
                      <td>{profile.sick_note_required_after_days ?? '—'} days</td>
                      <td><small>{profile.notes || '—'}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div class="alert alert-info" style={{ marginTop: '1rem' }}>
            <strong>Note:</strong> This is a reference guide. Actual entitlements are configured per leave type in the Leave Types tab.
          </div>
        </div>
      )}
    </div>
  );
}

function HolidaysTab({ holidays, locations, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    location_ids: [],
    color: '#10B981',
    recurring: true
  });
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [nextYearHolidays, setNextYearHolidays] = useState([]);
  const [loadingNextYear, setLoadingNextYear] = useState(false);

  // Load next year holidays when tab is selected
  useEffect(() => {
    if (selectedYear === nextYear && nextYearHolidays.length === 0 && !loadingNextYear) {
      setLoadingNextYear(true);
      getHolidays(nextYear)
        .then(data => setNextYearHolidays(data || []))
        .catch(err => console.error('Failed to load next year holidays:', err))
        .finally(() => setLoadingNextYear(false));
    }
  }, [selectedYear, nextYear]);

  function openAddModal() {
    setEditingHoliday(null);
    setFormData({ name: '', date: '', location_ids: [], color: '#10B981', recurring: true });
    setShowModal(true);
  }

  function openEditModal(holiday) {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      location_ids: (holiday.locations || []).map(l => l.id),
      color: holiday.color || '#10B981',
      recurring: !!holiday.recurring
    });
    setShowModal(true);
  }

  function toggleLocation(locId) {
    const newIds = formData.location_ids.includes(locId)
      ? formData.location_ids.filter(id => id !== locId)
      : [...formData.location_ids, locId];
    setFormData({ ...formData, location_ids: newIds });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (formData.location_ids.length === 0) {
      alert('Please select at least one location');
      return;
    }
    try {
      if (editingHoliday) {
        await updateHoliday(editingHoliday.id, formData);
      } else {
        await createHoliday(formData);
      }
      setShowModal(false);
      setFormData({ name: '', date: '', location_ids: [], color: '#10B981', recurring: true });
      setEditingHoliday(null);
      onRefresh();
      // Also reload next year if needed
      if (new Date(formData.date).getFullYear() === nextYear) {
        const data = await getHolidays(nextYear);
        setNextYearHolidays(data || []);
      }
    } catch (err) {
      alert(editingHoliday ? 'Failed to update holiday' : 'Failed to create holiday');
    }
  }

  async function handleDelete(holidayId) {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await deleteHoliday(holidayId);
      onRefresh();
      // Also reload next year
      const data = await getHolidays(nextYear);
      setNextYearHolidays(data || []);
    } catch (err) {
      alert('Failed to delete holiday');
    }
  }

  // Get holidays for selected year
  const displayHolidays = selectedYear === currentYear ? holidays : nextYearHolidays;

  // Group holidays by month
  const holidaysByMonth = displayHolidays.reduce((acc, h) => {
    const month = new Date(h.date).toLocaleDateString('en-GB', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(h);
    return acc;
  }, {});

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Public Holidays</h2>
          <p>Configure public holidays that affect leave calculations</p>
        </div>
        <button class="btn btn-primary" onClick={openAddModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Holiday
        </button>
      </div>

      {/* Year Tabs */}
      <div class="year-tabs">
        <button
          class={`btn ${selectedYear === currentYear ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSelectedYear(currentYear)}
        >
          {currentYear}
        </button>
        <button
          class={`btn ${selectedYear === nextYear ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSelectedYear(nextYear)}
        >
          {nextYear}
        </button>
      </div>

      {loadingNextYear && selectedYear === nextYear ? (
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      ) : Object.keys(holidaysByMonth).length > 0 ? (
        <div class="holidays-list">
          {Object.entries(holidaysByMonth).map(([month, monthHolidays]) => (
            <div key={month} class="holiday-month-group">
              <h3 class="month-header">{month}</h3>
              <div class="holiday-cards">
                {monthHolidays.map(holiday => (
                  <div key={holiday.id} class="holiday-card" onClick={() => openEditModal(holiday)} style="cursor: pointer;">
                    <div class="holiday-date">
                      <span class="day">{new Date(holiday.date).getDate()}</span>
                      <span class="weekday">{new Date(holiday.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                    </div>
                    <div class="holiday-info">
                      <h4>{holiday.name}</h4>
                      <span class="holiday-location">
                        {(holiday.locations || []).length > 0
                          ? (holiday.locations || []).map(l => l.name).join(', ')
                          : 'No locations'}
                        {!!holiday.recurring && <span class="recurring-badge">Recurring</span>}
                      </span>
                    </div>
                    <button class="btn-icon btn-danger" onClick={(e) => { e.stopPropagation(); handleDelete(holiday.id); }}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div class="empty-card full-width">
          <div class="empty-state-icon">🎉</div>
          <h3>No holidays configured for {selectedYear}</h3>
          <p>Add public holidays for accurate leave calculations</p>
          <button class="btn btn-primary" onClick={openAddModal}>Add First Holiday</button>
        </div>
      )}

      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal modal-scrollable" role="dialog" aria-modal="true">
            <form onSubmit={handleSubmit} class="flex-col max-h-screen-90 overflow-hidden">
              <div class="modal-header flex-shrink-0">
                <h2>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)} aria-label="Close modal">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body modal-body-scroll">
                <div class="form-group">
                  <label>Holiday Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Christmas Day, New Year's Day"
                  />
                </div>
                <div class="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    class="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div class="form-group">
                  <label>Applies to Locations</label>
                  <p class="form-hint">Select which locations this holiday applies to</p>
                  <div class="checkbox-group" style="display: grid; gap: 0.5rem; margin-top: 0.5rem;">
                    {locations.map(loc => (
                      <label key={loc.id} class="checkbox-label" style="display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem; border: 1px solid var(--border-color); border-radius: 6px; cursor: pointer;">
                        <input
                          type="checkbox"
                          checked={formData.location_ids.includes(loc.id)}
                          onChange={() => toggleLocation(loc.id)}
                        />
                        <span>{loc.name}</span>
                      </label>
                    ))}
                  </div>
                  {formData.location_ids.length === 0 && (
                    <p class="form-hint" style="color: var(--danger-color); margin-top: 0.5rem;">Select at least one location</p>
                  )}
                </div>
                <div class="form-group">
                  <label>Color</label>
                  <div class="flex gap-2 items-center">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ width: '50px', height: '36px', padding: '2px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      class="form-input"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      style={{ flex: 1 }}
                      placeholder="#10B981"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.recurring}
                      onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                    />
                    Recurring Annually
                  </label>
                </div>
              </div>
              <div class="modal-footer flex-shrink-0 bg-white">
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary">{editingHoliday ? 'Save Changes' : 'Create Holiday'}</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function NotificationsTab({ departments }) {
  const [settings, setSettings] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [settingsData, channelsData] = await Promise.all([
        getNotificationSettings(),
        getSlackChannels().catch(() => [])
      ]);
      setSettings(settingsData);
      setChannels(channelsData || []);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      await updateNotificationSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  }

  function updateSetting(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Team Notifications</h2>
          <p>Configure when and how your team receives notifications</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div class="notification-settings">
        <div class="card">
          <div class="card-header">
            <h3>Daily Summary</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.daily_summary}
                  onChange={(e) => updateSetting('daily_summary', e.target.checked)}
                />
                Send daily summary to Slack channel
              </label>
              <p class="form-hint">Get a daily overview of who's off today and upcoming leaves</p>
            </div>
            <div class="form-group">
              <label>Summary Time</label>
              <select
                class="form-select"
                value={settings?.daily_summary_time || '09:00'}
                onChange={(e) => updateSetting('daily_summary_time', e.target.value)}
                style="max-width: 150px;"
              >
                {Array.from({ length: 24 * 4 }, (_, i) => {
                  const hour = Math.floor(i / 4);
                  const minute = (i % 4) * 15;
                  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                  return <option key={time} value={time}>{time}</option>;
                })}
              </select>
            </div>
            <div class="form-group">
              <label>Slack Channel</label>
              <select
                class="form-select"
                value={settings?.slack_channel_id || ''}
                onChange={(e) => updateSetting('slack_channel_id', e.target.value)}
                style="max-width: 300px;"
              >
                <option value="">Select a channel...</option>
                {channels.map(ch => (
                  <option key={ch.id} value={ch.id}>
                    {ch.is_private ? '🔒 ' : '#'}{ch.name}
                  </option>
                ))}
              </select>
              {channels.length === 0 && (
                <p class="form-hint">No channels found. Make sure the bot is added to channels.</p>
              )}
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3>Weekly Summary</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.weekly_summary}
                  onChange={(e) => updateSetting('weekly_summary', e.target.checked)}
                />
                Send weekly summary
              </label>
              <p class="form-hint">Get a weekly digest of team availability</p>
            </div>
            <div class="form-group">
              <label>Day of Week</label>
              <select
                class="form-select"
                value={settings?.weekly_summary_day || 1}
                onChange={(e) => updateSetting('weekly_summary_day', parseInt(e.target.value))}
                style="max-width: 200px;"
              >
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
              </select>
            </div>
            <div class="form-group">
              <label>Time</label>
              <select
                class="form-select"
                value={settings?.weekly_summary_time || '09:00'}
                onChange={(e) => updateSetting('weekly_summary_time', e.target.value)}
                style="max-width: 150px;"
              >
                {/* Generate time options in 15-minute intervals */}
                {Array.from({ length: 24 * 4 }, (_, i) => {
                  const hour = Math.floor(i / 4);
                  const minute = (i % 4) * 15;
                  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                  return <option key={time} value={time}>{time}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3>Daily Weather Forecast</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.weather_forecast}
                  onChange={(e) => updateSetting('weather_forecast', e.target.checked)}
                />
                Send daily weather forecast
              </label>
              <p class="form-hint">Get weather forecasts for all locations with a city configured</p>
            </div>
            <div class="form-group">
              <label>Forecast Time</label>
              <select
                class="form-select"
                value={settings?.weather_forecast_time || '08:00'}
                onChange={(e) => updateSetting('weather_forecast_time', e.target.value)}
                style="max-width: 150px;"
              >
                {Array.from({ length: 24 * 4 }, (_, i) => {
                  const hour = Math.floor(i / 4);
                  const minute = (i % 4) * 15;
                  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                  return <option key={time} value={time}>{time}</option>;
                })}
              </select>
            </div>
            <p class="form-hint" style="margin-top: 0.5rem;">
              Weather is posted to the same channel configured above. Add a city to each location in the Locations tab to include it in the forecast.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3>Event Notifications</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_request}
                  onChange={(e) => updateSetting('notify_on_request', e.target.checked)}
                />
                Notify when leave is requested
              </label>
              <p class="form-hint">DM sent to approver(s). Sick leave also posts to channel immediately.</p>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_approval}
                  onChange={(e) => updateSetting('notify_on_approval', e.target.checked)}
                />
                Notify when leave is approved
              </label>
              <p class="form-hint">DM sent to the person who requested the leave.</p>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_rejection}
                  onChange={(e) => updateSetting('notify_on_rejection', e.target.checked)}
                />
                Notify when leave is rejected
              </label>
              <p class="form-hint">DM sent to the person who requested the leave.</p>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_upcoming_leaves}
                  onChange={(e) => updateSetting('notify_upcoming_leaves', e.target.checked)}
                />
                Notify about upcoming leaves (1 day before)
              </label>
              <p class="form-hint">DM sent to the person whose leave starts tomorrow.</p>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_holidays}
                  onChange={(e) => updateSetting('notify_holidays', e.target.checked)}
                />
                Notify about upcoming public holidays
              </label>
              <p class="form-hint">Posted to the configured Slack channel.</p>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_birthdays}
                  onChange={(e) => updateSetting('notify_birthdays', e.target.checked)}
                />
                Notify about team member birthdays
              </label>
              <p class="form-hint">Posted to the configured Slack channel.</p>
            </div>
          </div>
        </div>

        <button class="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>
    </div>
  );
}

function WorkspaceTab({ workspace, departments, locations, leaveTypes }) {
  const [settings, setSettings] = useState({
    max_carryover_days: 4,
    carryover_expiry_month: 3,  // Default: March
    carryover_expiry_day: 31,   // Default: 31st
    week_starts_on: 1,
    leave_year_start_month: 1,
    date_format: 'DD/MM/YYYY',
    // Import Users Configuration
    auto_import_users: true,
    import_guest_users: false,
    announce_new_users: 'slack',
    default_department_id: '',
    default_location_id: '',
    prorate_new_users: true,
    prorate_leave_type_ids: [],
    // Calendar Integration
    calendar_integration_enabled: true,
    // Sick Leave Settings
    allow_sick_leave_advance: false,
    // Holiday Settings
    deduct_leave_on_holidays: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const data = await getWorkspaceSettings();
      setSettings({
        max_carryover_days: data.max_carryover_days ?? 4,
        carryover_expiry_month: data.carryover_expiry_month ?? 3,
        carryover_expiry_day: data.carryover_expiry_day ?? 31,
        week_starts_on: data.week_starts_on ?? 1,
        leave_year_start_month: data.leave_year_start_month ?? 1,
        date_format: data.date_format ?? 'DD/MM/YYYY',
        // Import Users Configuration
        auto_import_users: data.auto_import_users ?? true,
        import_guest_users: data.import_guest_users ?? false,
        announce_new_users: data.announce_new_users ?? 'slack',
        default_department_id: data.default_department_id ?? '',
        default_location_id: data.default_location_id ?? '',
        prorate_new_users: data.prorate_new_users ?? true,
        prorate_leave_type_ids: data.prorate_leave_type_ids ?? [],
        // Calendar Integration
        calendar_integration_enabled: data.calendar_integration_enabled ?? true,
        // Sick Leave Settings
        allow_sick_leave_advance: data.allow_sick_leave_advance ?? false,
        // Holiday Settings
        deduct_leave_on_holidays: data.deduct_leave_on_holidays ?? false
      });
    } catch (error) {
      console.error('Failed to load workspace settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      await updateWorkspaceSettings(settings);
      setMessage({ type: 'success', text: 'Workspace settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings: ' + error.message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Workspace Settings</h2>
          <p>Configure your workspace preferences and leave policies</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div class="card">
        <div class="card-header">
          <h3>General Settings</h3>
        </div>
        <div class="card-body">
          <div style="max-width: 500px;">
            <div class="form-group">
              <label>Workspace Name</label>
              <input
                type="text"
                class="form-input"
                value={workspace?.name || ''}
                disabled
              />
            </div>

            <div class="form-group">
              <label>Plan</label>
              <div class="plan-display">
                <span class="plan-name">{workspace?.plan || 'Free'}</span>
                <a href="#" class="upgrade-link">Upgrade Plan</a>
              </div>
            </div>

            <div class="form-group">
              <label>Week Starts On</label>
              <select
                class="form-select"
                value={settings.week_starts_on}
                onChange={(e) => setSettings({ ...settings, week_starts_on: parseInt(e.target.value) })}
              >
                <option value="1">Monday</option>
                <option value="0">Sunday</option>
              </select>
            </div>

            <div class="form-group">
              <label>Leave Year Starts</label>
              <select
                class="form-select"
                value={settings.leave_year_start_month}
                onChange={(e) => setSettings({ ...settings, leave_year_start_month: parseInt(e.target.value) })}
              >
                <option value="1">January</option>
                <option value="4">April</option>
                <option value="7">July</option>
                <option value="10">October</option>
              </select>
            </div>

            <div class="form-group">
              <label>Date Format</label>
              <select
                class="form-select"
                value={settings.date_format}
                onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="card" style={{ marginTop: '1.5rem' }}>
        <div class="card-header">
          <h3>Leave Carryover Policy</h3>
        </div>
        <div class="card-body">
          <div style="max-width: 500px;">
            <div class="form-group">
              <label>Maximum Carryover Days</label>
              <input
                type="number"
                class="form-input"
                min="0"
                max="30"
                value={settings.max_carryover_days}
                onChange={(e) => setSettings({ ...settings, max_carryover_days: parseInt(e.target.value) || 0 })}
                style="max-width: 120px;"
              />
              <p class="form-hint">
                Maximum days of unused vacation leave that can be carried over to the next year.
                Malta's statutory limit is 4 days.
              </p>
            </div>

            <div class="form-group" style={{ marginTop: '1rem' }}>
              <label>Carryover Expiry Date</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  class="form-select"
                  value={settings.carryover_expiry_month}
                  onChange={(e) => setSettings({ ...settings, carryover_expiry_month: parseInt(e.target.value) })}
                  style={{ width: '140px' }}
                >
                  <option value={1}>January</option>
                  <option value={2}>February</option>
                  <option value={3}>March</option>
                  <option value={4}>April</option>
                  <option value={5}>May</option>
                  <option value={6}>June</option>
                  <option value={7}>July</option>
                  <option value={8}>August</option>
                  <option value={9}>September</option>
                  <option value={10}>October</option>
                  <option value={11}>November</option>
                  <option value={12}>December</option>
                </select>
                <select
                  class="form-select"
                  value={settings.carryover_expiry_day}
                  onChange={(e) => setSettings({ ...settings, carryover_expiry_day: parseInt(e.target.value) })}
                  style={{ width: '80px' }}
                >
                  {[...Array(31)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <p class="form-hint">
                Date by which carried over days must be used or they expire.
                Default is March 31st (end of Q1).
              </p>
            </div>

            <div class="info-box" style={{
              backgroundColor: 'var(--color-info-bg, #e0f2fe)',
              border: '1px solid var(--color-info-border, #7dd3fc)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                How carryover works:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                <li>On January 1st, unused vacation days (up to {settings.max_carryover_days} days) are automatically carried over</li>
                <li>Carried over days must be used by {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][settings.carryover_expiry_month - 1]} {settings.carryover_expiry_day} or they expire</li>
                <li>Set to 0 to disable carryover entirely</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="card" style={{ marginTop: '1.5rem' }}>
        <div class="card-header">
          <h3>Leave Rules</h3>
        </div>
        <div class="card-body">
          <div style="max-width: 500px; display: grid; gap: 1.5rem;">
            <div>
              <label class="toggle-label">
                <input
                  type="checkbox"
                  checked={settings.allow_sick_leave_advance}
                  onChange={(e) => setSettings({ ...settings, allow_sick_leave_advance: e.target.checked })}
                />
                <span class="toggle-text">Allow requesting sick days in advance</span>
              </label>
              <p class="form-hint">
                By default, sick leave can only be registered for today or previous days.
                Enable this to allow employees to book sick leave for future dates.
              </p>
            </div>

            <div>
              <label class="toggle-label">
                <input
                  type="checkbox"
                  checked={settings.deduct_leave_on_holidays}
                  onChange={(e) => setSettings({ ...settings, deduct_leave_on_holidays: e.target.checked })}
                />
                <span class="toggle-text">Deduct leave allowance for public holidays</span>
              </label>
              <p class="form-hint">
                By default, leave is NOT deducted for days that fall on public holidays.
                Enable this to deduct leave even when an employee takes leave on a public holiday.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="card" style={{ marginTop: '1.5rem' }}>
        <div class="card-header">
          <h3>Import Users Configuration</h3>
        </div>
        <div class="card-body">
          <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
            Configure automatic user import from your Slack workspace and set default settings for new users.
          </p>

          <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '600px' }}>
            {/* Auto import users */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Do you want to import all users from Slack Workspace automatically?
                <span title="When enabled, new Slack users are automatically imported" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <div class="toggle-group" style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  class={`toggle-btn ${settings.auto_import_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, auto_import_users: true })}
                >
                  Yes
                </button>
                <button
                  type="button"
                  class={`toggle-btn ${!settings.auto_import_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, auto_import_users: false })}
                >
                  No
                </button>
              </div>
            </div>

            {/* Import guest users */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Do you want to import multichannel guest users?
                <span title="Guest users have limited access to specific channels" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <div class="toggle-group" style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  class={`toggle-btn ${settings.import_guest_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, import_guest_users: true })}
                >
                  Yes
                </button>
                <button
                  type="button"
                  class={`toggle-btn ${!settings.import_guest_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, import_guest_users: false })}
                >
                  No
                </button>
              </div>
            </div>

            {/* Announce to new users */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Announce to new users
                <span title="How new users are notified about being added" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <select
                class="form-select"
                value={settings.announce_new_users}
                onChange={(e) => setSettings({ ...settings, announce_new_users: e.target.value })}
                style={{ maxWidth: '300px' }}
              >
                <option value="slack">Yes, via Slack message</option>
                <option value="none">No announcement</option>
              </select>
            </div>

            {/* Default Department */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Department
                <span title="New users will be assigned to this department by default" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <select
                class="form-select"
                value={settings.default_department_id}
                onChange={(e) => setSettings({ ...settings, default_department_id: e.target.value })}
                style={{ maxWidth: '300px' }}
              >
                <option value="">No default department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            {/* Default Location */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Location
                <span title="New users will be assigned to this location by default" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <select
                class="form-select"
                value={settings.default_location_id}
                onChange={(e) => setSettings({ ...settings, default_location_id: e.target.value })}
                style={{ maxWidth: '300px' }}
              >
                <option value="">No default location</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            {/* Prorate new users */}
            <div class="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Prorate the new users' quota for this year?
                <span title="Calculate leave entitlement based on when the user joined" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
              </label>
              <div class="toggle-group" style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  class={`toggle-btn ${settings.prorate_new_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, prorate_new_users: true })}
                >
                  Yes
                </button>
                <button
                  type="button"
                  class={`toggle-btn ${!settings.prorate_new_users ? 'active' : ''}`}
                  onClick={() => setSettings({ ...settings, prorate_new_users: false })}
                >
                  No
                </button>
              </div>
            </div>

            {/* Prorate leave types */}
            {settings.prorate_new_users && (
              <div class="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  Apply prorating to the following leave types
                  <span title="Select which leave types should be prorated for new users" style={{ cursor: 'help', color: '#9CA3AF' }}>ⓘ</span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {leaveTypes.map(lt => {
                    const isSelected = settings.prorate_leave_type_ids.includes(lt.id);
                    return (
                      <button
                        key={lt.id}
                        type="button"
                        onClick={() => {
                          const newIds = isSelected
                            ? settings.prorate_leave_type_ids.filter(id => id !== lt.id)
                            : [...settings.prorate_leave_type_ids, lt.id];
                          setSettings({ ...settings, prorate_leave_type_ids: newIds });
                        }}
                        style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '9999px',
                          border: '1px solid',
                          borderColor: isSelected ? '#8B5CF6' : '#E5E7EB',
                          backgroundColor: isSelected ? '#F3E8FF' : 'white',
                          color: isSelected ? '#7C3AED' : '#374151',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        {lt.name}
                        {isSelected && <span class="text-xs">✕</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div class="card" style={{ marginTop: '1.5rem' }}>
        <div class="card-header">
          <h3>Calendar Integration</h3>
        </div>
        <div class="card-body">
          <div>
            <div class="form-group">
              <div class="settings-toggle-row">
                <div>
                  <label style={{ marginBottom: 0 }}>Enable Calendar Sync</label>
                  <p class="form-hint" style={{ marginTop: '0.25rem' }}>
                    Allow users to connect their personal Google or Microsoft calendars
                  </p>
                </div>
                <div class="toggle-group">
                  <button
                    type="button"
                    class={`toggle-btn ${settings.calendar_integration_enabled ? 'active' : ''}`}
                    onClick={() => setSettings({ ...settings, calendar_integration_enabled: true })}
                  >
                    Enabled
                  </button>
                  <button
                    type="button"
                    class={`toggle-btn ${!settings.calendar_integration_enabled ? 'active' : ''}`}
                    onClick={() => setSettings({ ...settings, calendar_integration_enabled: false })}
                  >
                    Disabled
                  </button>
                </div>
              </div>
            </div>

            <div class="info-box" style={{
              backgroundColor: 'var(--color-info-bg, #e0f2fe)',
              border: '1px solid var(--color-info-border, #7dd3fc)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                When enabled:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                <li>Users can connect their Google Calendar or Microsoft Outlook from the Calendar page</li>
                <li>Approved leave is automatically synced to connected calendars</li>
                <li>Users can opt-in to receive team members' leave in their calendar</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <button
        class="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: '1.5rem' }}
      >
        {saving ? 'Saving...' : 'Save Workspace Settings'}
      </button>
    </div>
  );
}

function OpeningBalancesTab({ allUsers, leaveTypes }) {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [editValue, setEditValue] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadBalances();
  }, [selectedYear]);

  async function loadBalances() {
    setLoading(true);
    try {
      const data = await getAllUserBalances(selectedYear);
      setBalances(data || []);
    } catch (error) {
      console.error('Failed to load balances:', error);
      setMessage({ type: 'error', text: 'Failed to load balances' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveBalance(userId, leaveTypeId, field, value) {
    setSaving(`${userId}-${leaveTypeId}`);
    setMessage(null);
    try {
      await updateUserBalance(userId, leaveTypeId, selectedYear, { [field]: parseFloat(value) || 0 });
      setMessage({ type: 'success', text: 'Balance updated successfully!' });
      setEditingBalance(null);
      loadBalances();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update balance: ' + error.message });
    } finally {
      setSaving(null);
    }
  }

  const [initialising, setInitialising] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  async function handleInitialiseYear() {
    if (!confirm(`This will create balance records for all users and leave types for ${selectedYear}. Continue?`)) {
      return;
    }
    setInitialising(true);
    setMessage(null);
    try {
      const result = await initialiseBalances(selectedYear);
      setMessage({ type: 'success', text: result.message });
      loadBalances();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to initialise: ' + error.message });
    } finally {
      setInitialising(false);
    }
  }

  async function handleRecalculateEntitlements() {
    if (!confirm(`This will update all entitlements for ${selectedYear} to match current department policies. Continue?`)) {
      return;
    }
    setRecalculating(true);
    setMessage(null);
    try {
      const result = await recalculateBalances(selectedYear);
      setMessage({ type: 'success', text: result.message });
      loadBalances();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to recalculate: ' + error.message });
    } finally {
      setRecalculating(false);
    }
  }

  function startEditing(userId, leaveTypeId, field, currentValue) {
    setEditingBalance(`${userId}-${leaveTypeId}-${field}`);
    setEditValue(currentValue?.toString() || '0');
  }

  function cancelEditing() {
    setEditingBalance(null);
    setEditValue('');
  }

  // Group balances by user
  const balancesByUser = {};
  balances.forEach(b => {
    if (!balancesByUser[b.user_id]) {
      balancesByUser[b.user_id] = {
        user_id: b.user_id,
        user_name: b.user_name,
        avatar_url: b.avatar_url,
        department_name: b.department_name,
        balances: {}
      };
    }
    balancesByUser[b.user_id].balances[b.leave_type_id] = b;
  });

  // Filter users by search query
  const filteredUsers = Object.values(balancesByUser).filter(u =>
    !searchQuery || u.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get active users who don't have balances yet
  const usersWithBalances = Object.keys(balancesByUser);
  const usersWithoutBalances = allUsers.filter(u =>
    u.is_active && !usersWithBalances.includes(u.id) &&
    (!searchQuery || u.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Opening Balances</h2>
          <p>Set opening balances for employees who joined mid-year or to adjust balances</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div class="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Year</label>
          <select
            class="form-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ width: '120px' }}
          >
            <option value={currentYear - 1}>{currentYear - 1}</option>
            <option value={currentYear}>{currentYear}</option>
            <option value={currentYear + 1}>{currentYear + 1}</option>
          </select>
        </div>
        <div class="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px', maxWidth: '300px' }}>
          <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Search</label>
          <input
            type="text"
            class="form-input"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div class="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
          <button
            class="btn btn-secondary"
            onClick={handleInitialiseYear}
            disabled={initialising}
            title={`Create balance records for all users and leave types for ${selectedYear}`}
          >
            {initialising ? 'Initialising...' : `Initialise ${selectedYear}`}
          </button>
          <button
            class="btn btn-outline"
            onClick={handleRecalculateEntitlements}
            disabled={recalculating}
            title={`Update entitlements for ${selectedYear} based on current department policies`}
          >
            {recalculating ? 'Recalculating...' : 'Recalculate Entitlements'}
          </button>
        </div>
      </div>

      <div class="card">
        <div class="card-body" style={{ padding: 0, overflowX: 'auto' }}>
          <table class="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Employee</th>
                {leaveTypes.filter(lt => lt.is_active !== false).map(lt => (
                  <th key={lt.id} style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', minWidth: '150px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                      {lt.icon} {lt.name}
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>({({ hours: 'hrs', days: 'days', weeks: 'wks', months: 'mths' })[lt.unit] || 'days'})</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <tr key={user.user_id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div class="avatar avatar-sm">
                          {user.avatar_url ? <img src={user.avatar_url} alt="" /> : user.user_name?.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.user_name}</div>
                          {user.department_name && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{user.department_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => {
                      const balance = user.balances[lt.id];
                      const editKey = `${user.user_id}-${lt.id}-carried_over`;
                      const isEditing = editingBalance === editKey;
                      const isSaving = saving === `${user.user_id}-${lt.id}`;
                      const unitLabel = ({ hours: 'hrs', days: 'days', weeks: 'wks', months: 'mths' })[lt.unit] || 'days';

                      return (
                        <td key={lt.id} style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                          {balance ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Entitlement: <strong>{balance.entitlement || 0}</strong> <span class="text-xs">{unitLabel}</span>
                              </div>
                              {(balance.adjustment || 0) !== 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#8b5cf6' }} title="Compensation for public holidays falling on weekends">
                                  + Adjustment: <strong>{balance.adjustment > 0 ? '+' : ''}{balance.adjustment}</strong> <span style={{ fontSize: '0.7rem' }}>{unitLabel}</span>
                                  <span style={{ marginLeft: '0.25rem', cursor: 'help' }} title="Weekend public holiday compensation">ⓘ</span>
                                </div>
                              )}
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Used: <strong>{balance.used || 0}</strong> <span class="text-xs">{unitLabel}</span>
                              </div>
                              <div class="flex items-center gap-2">
                                <span style={{ fontSize: '0.875rem', color: '#059669' }}>Opening:</span>
                                {isEditing ? (
                                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                    <input
                                      type="number"
                                      class="form-input"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      style={{ width: '70px', padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                      step={lt.unit === 'hours' ? '0.5' : '0.5'}
                                      min="0"
                                      autoFocus
                                    />
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{unitLabel}</span>
                                    <button
                                      class="btn-icon"
                                      onClick={() => handleSaveBalance(user.user_id, lt.id, 'carried_over', editValue)}
                                      disabled={isSaving}
                                      style={{ color: '#059669' }}
                                    >
                                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </button>
                                    <button
                                      class="btn-icon"
                                      onClick={cancelEditing}
                                      disabled={isSaving}
                                      style={{ color: '#ef4444' }}
                                    >
                                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    class="balance-edit-btn"
                                    onClick={() => startEditing(user.user_id, lt.id, 'carried_over', balance.carried_over)}
                                    style={{
                                      background: 'none',
                                      border: '1px dashed #d1d5db',
                                      borderRadius: '4px',
                                      padding: '0.125rem 0.5rem',
                                      cursor: 'pointer',
                                      fontWeight: '500',
                                      color: '#059669'
                                    }}
                                  >
                                    {balance.carried_over || 0} <span style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>{unitLabel}</span>
                                  </button>
                                )}
                              </div>
                              <div
                                style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem', cursor: 'help' }}
                                title={`${balance.entitlement || 0} (entitlement)${(balance.adjustment || 0) !== 0 ? ` + ${balance.adjustment} (weekend holiday comp.)` : ''}${(balance.carried_over || 0) !== 0 ? ` + ${balance.carried_over} (carried over)` : ''}${(balance.accrued || 0) !== 0 ? ` + ${balance.accrued} (accrued)` : ''} - ${balance.used || 0} (used) = ${balance.available ?? ((balance.entitlement || 0) + (balance.accrued || 0) + (balance.carried_over || 0) + (balance.adjustment || 0) - (balance.used || 0))}`}
                              >
                                Available: <strong style={{ color: '#3b82f6' }}>{balance.available ?? ((balance.entitlement || 0) + (balance.accrued || 0) + (balance.carried_over || 0) + (balance.adjustment || 0) - (balance.used || 0))}</strong> {unitLabel}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No balance</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : usersWithoutBalances.length > 0 ? (
                usersWithoutBalances.slice(0, 10).map(user => (
                  <tr key={user.id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div class="avatar avatar-sm">
                          {user.avatar_url ? <img src={user.avatar_url} alt="" /> : user.name?.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>No balances set</div>
                        </div>
                      </div>
                    </td>
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => (
                      <td key={lt.id} style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={leaveTypes.length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div class="info-box" style={{
        backgroundColor: '#e0f2fe',
        border: '1px solid #7dd3fc',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1.5rem'
      }}>
        <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
          About Opening Balances:
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
          <li><strong>Opening Balance</strong> is added to the employee's entitlement</li>
          <li>Use this to set carried over days from previous years</li>
          <li>Or to adjust balances for employees who joined mid-year</li>
          <li>Click on the number to edit the opening balance</li>
        </ul>
      </div>
    </div>
  );
}

function DataManagementTab() {
  const { hasFeature } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Google Drive Backup state
  const [driveBackup, setDriveBackup] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [driveProcessing, setDriveProcessing] = useState(false);
  const [driveFolders, setDriveFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [justConnected, setJustConnected] = useState(false);

  // Restore state
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreData, setRestoreData] = useState(null);
  const [restoreConfirmCode, setRestoreConfirmCode] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  // Typed confirmation for destructive actions
  const [clearUsersConfirmText, setClearUsersConfirmText] = useState('');
  const [clearHistoryConfirmText, setClearHistoryConfirmText] = useState('');

  useEffect(() => {
    loadStats();
    loadDriveBackup();

    // Check for success message from OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_drive') === 'connected') {
      setJustConnected(true);
      // Clean up URL but keep on this page
      window.history.replaceState({}, '', '/settings?tab=data');
    }
  }, []);

  async function loadStats() {
    try {
      const data = await getDataStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load data stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadDriveBackup() {
    if (!hasFeature('google_drive_backup')) {
      setDriveLoading(false);
      return;
    }
    try {
      const data = await getGoogleDriveBackup();
      setDriveBackup(data);
      // Load folders if connected
      if (data?.connected) {
        loadDriveFolders();
      }
    } catch (error) {
      // Feature not available or other error - silently ignore
      console.error('Failed to load Google Drive backup settings:', error);
    } finally {
      setDriveLoading(false);
    }
  }

  async function loadDriveFolders() {
    setFoldersLoading(true);
    try {
      const data = await getGoogleDriveFolders();
      setDriveFolders(data.folders || []);
    } catch (error) {
      console.error('Failed to load Drive folders:', error);
    } finally {
      setFoldersLoading(false);
    }
  }

  async function handleConnectDrive() {
    setDriveProcessing(true);
    try {
      const result = await connectGoogleDrive();
      if (result.auth_url) {
        window.location.href = result.auth_url;
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to connect Google Drive: ' + error.message });
    } finally {
      setDriveProcessing(false);
    }
  }

  async function handleDisconnectDrive() {
    setDriveProcessing(true);
    try {
      await disconnectGoogleDrive();
      setDriveBackup({ connected: false, settings: { enabled: false } });
      setMessage({ type: 'success', text: 'Google Drive disconnected' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to disconnect: ' + error.message });
    } finally {
      setDriveProcessing(false);
    }
  }

  async function handleUpdateDriveSettings(settings) {
    setDriveProcessing(true);
    try {
      const result = await updateGoogleDriveSettings(settings);
      setDriveBackup(prev => ({ ...prev, settings: result.settings }));
      setMessage({ type: 'success', text: 'Backup settings updated' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings: ' + error.message });
    } finally {
      setDriveProcessing(false);
    }
  }

  async function handleRunBackup() {
    setDriveProcessing(true);
    setMessage(null);
    try {
      await runGoogleDriveBackup();
      await loadDriveBackup(); // Refresh to get updated last backup time
      setMessage({ type: 'success', text: 'Backup completed successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Backup failed: ' + error.message });
    } finally {
      setDriveProcessing(false);
    }
  }

  async function handleDownloadBackup() {
    setProcessing(true);
    setMessage(null);
    try {
      await downloadBackup();
      setMessage({ type: 'success', text: 'Backup downloaded successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to download backup: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  async function handleClearUsers() {
    if (clearUsersConfirmText !== 'DELETE') return;
    setProcessing(true);
    setMessage(null);
    setConfirmAction(null);
    setClearUsersConfirmText('');
    try {
      const result = await clearNonAdminUsers();
      setMessage({ type: 'success', text: `Removed ${result.deletedCount} non-admin users` });
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear users: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  async function handleClearHistory() {
    if (clearHistoryConfirmText !== 'DELETE') return;
    setProcessing(true);
    setMessage(null);
    setConfirmAction(null);
    setClearHistoryConfirmText('');
    try {
      const result = await clearLeaveHistory();
      setMessage({ type: 'success', text: `Cleared ${result.deletedLeaveRequests} leave requests and reset ${result.resetBalances} balances` });
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear history: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Data Management</h2>
          <p>Backup your data or clear workspace information</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      {/* Data Statistics */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>Current Data</h3>
        </div>
        <div class="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-primary, #3b82f6)' }}>{stats?.users?.total || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Total Users</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-warning, #f59e0b)' }}>{stats?.users?.admins || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Admins</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-success, #10b981)' }}>{stats?.users?.regular || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Regular Users</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-info, #6366f1)' }}>{stats?.leaveRequests?.total || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Leave Requests</div>
            </div>
          </div>
        </div>
      </div>

      {/* Backup */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>Backup Data</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Download a complete backup of all workspace data including users, leave types, departments, locations, holidays, leave requests, and balances.
          </p>
          <button
            class="btn btn-primary"
            onClick={handleDownloadBackup}
            disabled={processing}
          >
            {processing ? 'Downloading...' : 'Download Backup'}
          </button>
        </div>
      </div>

      {/* Google Drive Automatic Backup */}
      {hasFeature('google_drive_backup') && (
        <div class="card" style={{ marginBottom: '1.5rem' }}>
          <div class="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#4285F4">
                <path d="M7.71 3.5L1.15 15l3.43 5.93h13.82L21.84 15 15.29 3.5H7.71zm.86 1.5h5.86L20.07 15l-2.57 4.43H6.5L3.93 15l5.64-10z"/>
                <path d="M12 8L8.5 14h7L12 8z"/>
              </svg>
              Google Drive Backup
              <span style={{
                fontSize: '0.75rem',
                padding: '0.125rem 0.5rem',
                backgroundColor: 'var(--color-primary-bg, #eef2ff)',
                color: 'var(--color-primary, #6366f1)',
                borderRadius: '9999px',
                fontWeight: '500'
              }}>Business</span>
            </h3>
          </div>
          <div class="card-body">
            {driveLoading ? (
              <p style={{ color: 'var(--color-text-secondary)' }}>Loading...</p>
            ) : !driveBackup?.connected ? (
              <>
                <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  Connect your Google Drive to automatically backup your workspace data daily. Backups are stored in a folder called "Unplug Backups" in your Drive.
                </p>
                <button
                  class="btn btn-primary"
                  onClick={handleConnectDrive}
                  disabled={driveProcessing}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M7.71 3.5L1.15 15l3.43 5.93h13.82L21.84 15 15.29 3.5H7.71zm.86 1.5h5.86L20.07 15l-2.57 4.43H6.5L3.93 15l5.64-10z"/>
                  </svg>
                  {driveProcessing ? 'Connecting...' : 'Connect Google Drive'}
                </button>
              </>
            ) : (
              <>
                {/* Success banner when just connected */}
                {justConnected && (
                  <div style={{
                    marginBottom: '1.5rem',
                    padding: '1.25rem',
                    backgroundColor: 'var(--color-success-bg, #ecfdf5)',
                    border: '1px solid var(--color-success, #10b981)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <svg viewBox="0 0 20 20" fill="var(--color-success, #10b981)" width="24" height="24">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <strong style={{ fontSize: '1.1rem', color: 'var(--color-success-dark, #047857)' }}>
                        Google Drive Connected Successfully!
                      </strong>
                    </div>
                    <p style={{ color: 'var(--color-success-dark, #047857)', marginBottom: '0.75rem' }}>
                      Your Google Drive is now linked. Here's what to do next:
                    </p>
                    <ol style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--color-success-dark, #047857)' }}>
                      <li style={{ marginBottom: '0.25rem' }}>Choose a backup location below (optional - defaults to My Drive root)</li>
                      <li style={{ marginBottom: '0.25rem' }}>Enable automatic daily backups</li>
                      <li>Set your preferred backup time</li>
                    </ol>
                    <button
                      style={{
                        marginTop: '1rem',
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.875rem',
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-success-dark, #047857)',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      onClick={() => setJustConnected(false)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Normal connected status when not just connected */}
                {!justConnected && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--color-success-bg, #ecfdf5)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg viewBox="0 0 20 20" fill="var(--color-success, #10b981)" width="20" height="20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Connected as <strong>{driveBackup.email}</strong></span>
                  </div>
                )}

                <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
                  {/* Backup Location */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text-secondary)', fontWeight: '500' }}>
                      Backup location:
                    </label>
                    <select
                      class="form-select"
                      value={driveBackup.settings?.folderId || 'root'}
                      onChange={(e) => {
                        const folder = driveFolders.find(f => f.id === e.target.value);
                        handleUpdateDriveSettings({
                          folderId: e.target.value,
                          folderName: folder?.name || 'My Drive (root)',
                          folderType: folder?.type || 'my_drive'
                        });
                      }}
                      disabled={driveProcessing || foldersLoading}
                      style={{ minWidth: '280px' }}
                    >
                      {foldersLoading ? (
                        <option>Loading folders...</option>
                      ) : driveFolders.length > 0 ? (
                        driveFolders.map(folder => (
                          <option key={folder.id} value={folder.id}>
                            {folder.name}
                          </option>
                        ))
                      ) : (
                        <option value="root">My Drive (root)</option>
                      )}
                    </select>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                      Backups will be saved in an "Unplug Backups" folder within the selected location.
                    </p>
                  </div>

                  {/* Enable/Disable Toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={driveBackup.settings?.enabled}
                      onChange={(e) => handleUpdateDriveSettings({ enabled: e.target.checked })}
                      disabled={driveProcessing}
                      style={{ width: '1.25rem', height: '1.25rem' }}
                    />
                    <span>Enable automatic daily backups</span>
                  </label>

                  {/* Backup Time */}
                  {driveBackup.settings?.enabled && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label style={{ color: 'var(--color-text-secondary)' }}>Backup time:</label>
                      <input
                        type="time"
                        value={driveBackup.settings?.time || '02:00'}
                        onChange={(e) => handleUpdateDriveSettings({ time: e.target.value })}
                        disabled={driveProcessing}
                        class="form-input"
                        style={{ width: 'auto' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>(workspace timezone)</span>
                    </div>
                  )}

                  {/* Last Backup Status */}
                  {driveBackup.settings?.lastBackup && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      Last backup: {new Date(driveBackup.settings.lastBackup).toLocaleString()}
                      {driveBackup.settings.lastStatus === 'success' && (
                        <span style={{ color: 'var(--color-success)', marginLeft: '0.5rem' }}>Success</span>
                      )}
                      {driveBackup.settings.lastStatus === 'failed' && (
                        <span style={{ color: 'var(--color-danger)', marginLeft: '0.5rem' }}>
                          Failed: {driveBackup.settings.lastError}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    class="btn btn-primary"
                    onClick={handleRunBackup}
                    disabled={driveProcessing}
                  >
                    {driveProcessing ? 'Running...' : 'Run Backup Now'}
                  </button>
                  <button
                    class="btn btn-secondary"
                    onClick={handleDisconnectDrive}
                    disabled={driveProcessing}
                  >
                    Disconnect
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upgrade prompt for non-Business users */}
      {!hasFeature('google_drive_backup') && (
        <div class="card" style={{ marginBottom: '1.5rem', opacity: 0.7 }}>
          <div class="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#9ca3af">
                <path d="M7.71 3.5L1.15 15l3.43 5.93h13.82L21.84 15 15.29 3.5H7.71zm.86 1.5h5.86L20.07 15l-2.57 4.43H6.5L3.93 15l5.64-10z"/>
              </svg>
              Google Drive Backup
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ color: 'var(--color-text-secondary)' }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </h3>
          </div>
          <div class="card-body">
            <p style={{ color: 'var(--color-text-secondary, #6b7280)', marginBottom: '1rem' }}>
              Automatic daily backups to Google Drive is available on the Business plan.
            </p>
            <a href="/billing" class="btn btn-primary">Upgrade to Business</a>
          </div>
        </div>
      )}

      {/* Clear Users */}
      <div class="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--color-danger, #ef4444)' }}>
        <div class="card-header" style={{ borderBottomColor: 'var(--color-danger-light, #fecaca)' }}>
          <h3 style={{ color: 'var(--color-danger, #ef4444)' }}>Clear Non-Admin Users</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Remove all users except admins and owners. This will delete {stats?.users?.regular || 0} users, their leave balances, and any pending leave requests.
          </p>
          <div class="info-box" style={{
            backgroundColor: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-light, #fecaca)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Warning:</strong> This action cannot be undone. Make sure to download a backup first.
          </div>
          {confirmAction === 'users' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--color-danger-bg, #fef2f2)',
                borderRadius: '8px',
                border: '2px solid var(--color-danger, #ef4444)'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--color-danger, #ef4444)' }}>
                  Type DELETE to confirm
                </p>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  This will permanently remove {stats?.users?.regular || 0} users and all their data.
                </p>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Type DELETE to confirm"
                  value={clearUsersConfirmText}
                  onChange={(e) => setClearUsersConfirmText(e.target.value.toUpperCase())}
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    borderColor: clearUsersConfirmText === 'DELETE' ? 'var(--color-success, #10b981)' : undefined
                  }}
                  disabled={processing}
                />
              </div>
              <div class="flex gap-2">
                <button
                  class="btn btn-danger"
                  onClick={handleClearUsers}
                  disabled={processing || clearUsersConfirmText !== 'DELETE'}
                >
                  {processing ? 'Clearing...' : 'Confirm Delete Users'}
                </button>
                <button
                  class="btn btn-secondary"
                  onClick={() => { setConfirmAction(null); setClearUsersConfirmText(''); }}
                  disabled={processing}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              class="btn btn-danger-outline"
              onClick={() => setConfirmAction('users')}
              disabled={processing || stats?.users?.regular === 0}
            >
              Clear Non-Admin Users
            </button>
          )}
        </div>
      </div>

      {/* Clear Leave History */}
      <div class="card" style={{ borderColor: 'var(--color-danger, #ef4444)' }}>
        <div class="card-header" style={{ borderBottomColor: 'var(--color-danger-light, #fecaca)' }}>
          <h3 style={{ color: 'var(--color-danger, #ef4444)' }}>Clear Leave History</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Remove all leave requests and reset all leave balances to zero. This will delete {stats?.leaveRequests?.total || 0} leave requests.
          </p>
          <div class="info-box" style={{
            backgroundColor: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-light, #fecaca)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Warning:</strong> This action cannot be undone. All leave history will be permanently deleted and balances reset.
          </div>
          {confirmAction === 'history' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--color-danger-bg, #fef2f2)',
                borderRadius: '8px',
                border: '2px solid var(--color-danger, #ef4444)'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--color-danger, #ef4444)' }}>
                  Type DELETE to confirm
                </p>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  This will permanently delete {stats?.leaveRequests?.total || 0} leave requests and reset all balances.
                </p>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Type DELETE to confirm"
                  value={clearHistoryConfirmText}
                  onChange={(e) => setClearHistoryConfirmText(e.target.value.toUpperCase())}
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    borderColor: clearHistoryConfirmText === 'DELETE' ? 'var(--color-success, #10b981)' : undefined
                  }}
                  disabled={processing}
                />
              </div>
              <div class="flex gap-2">
                <button
                  class="btn btn-danger"
                  onClick={handleClearHistory}
                  disabled={processing || clearHistoryConfirmText !== 'DELETE'}
                >
                  {processing ? 'Clearing...' : 'Confirm Clear History'}
                </button>
                <button
                  class="btn btn-secondary"
                  onClick={() => { setConfirmAction(null); setClearHistoryConfirmText(''); }}
                  disabled={processing}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              class="btn btn-danger-outline"
              onClick={() => setConfirmAction('history')}
              disabled={processing || stats?.leaveRequests?.total === 0}
            >
              Clear Leave History
            </button>
          )}
        </div>
      </div>

      {/* Restore from Backup */}
      <div class="card" style={{ marginTop: '1.5rem', borderColor: 'var(--color-warning, #f59e0b)' }}>
        <div class="card-header" style={{ borderBottomColor: 'var(--color-warning-light, #fde68a)' }}>
          <h3 style={{ color: 'var(--color-warning-dark, #b45309)' }}>Restore from Backup</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Upload a backup JSON file to restore your workspace data. This will replace all existing data.
          </p>
          <div class="info-box" style={{
            backgroundColor: 'var(--color-warning-bg, #fffbeb)',
            border: '1px solid var(--color-warning-light, #fde68a)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Warning:</strong> Restoring will replace all current users, leave types, leave requests, departments, and balances. Only the workspace owner can perform this action.
          </div>

          {restoreResult && (
            <div class={`alert alert-${restoreResult.success ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
              {restoreResult.success ? (
                <div>
                  <strong>Restore completed successfully!</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                    {Object.entries(restoreResult.stats || {}).map(([key, value]) => (
                      <li key={key}>{key}: {value} records</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <span>{restoreResult.error}</span>
              )}
            </div>
          )}

          {!restoreData ? (
            <div>
              <input
                type="file"
                accept=".json"
                id="restore-file-input"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setRestoreFile(file);
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      try {
                        const data = JSON.parse(event.target.result);
                        if (!data.version || !data.workspace) {
                          setMessage({ type: 'error', text: 'Invalid backup file format' });
                          setRestoreFile(null);
                          return;
                        }
                        setRestoreData(data);
                        setRestoreResult(null);
                      } catch (err) {
                        setMessage({ type: 'error', text: 'Failed to parse backup file: ' + err.message });
                        setRestoreFile(null);
                      }
                    };
                    reader.readAsText(file);
                  }
                }}
              />
              <button
                class="btn btn-warning-outline"
                onClick={() => document.getElementById('restore-file-input')?.click()}
                disabled={restoring}
              >
                Select Backup File
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
                borderRadius: '8px',
                border: '1px solid var(--color-border, #e5e7eb)'
              }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>Backup Details</p>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  <li>File: {restoreFile?.name}</li>
                  <li>Version: {restoreData.version}</li>
                  <li>Created: {new Date(restoreData.exportedAt).toLocaleString()}</li>
                  <li>Users: {restoreData.users?.length || 0}</li>
                  <li>Leave Types: {restoreData.leaveTypes?.length || 0}</li>
                  <li>Leave Requests: {restoreData.leaveRequests?.length || 0}</li>
                  <li>Departments: {restoreData.departments?.length || 0}</li>
                </ul>
              </div>

              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--color-danger-bg, #fef2f2)',
                borderRadius: '8px',
                border: '2px solid var(--color-danger, #ef4444)'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--color-danger, #ef4444)' }}>
                  Type RESTORE to confirm
                </p>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  This will permanently replace all existing data with the backup.
                </p>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Type RESTORE to confirm"
                  value={restoreConfirmCode}
                  onChange={(e) => setRestoreConfirmCode(e.target.value.toUpperCase())}
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    borderColor: restoreConfirmCode === 'RESTORE' ? 'var(--color-success, #10b981)' : undefined
                  }}
                  disabled={restoring}
                />
              </div>

              <div class="flex gap-2">
                <button
                  class="btn btn-danger"
                  onClick={async () => {
                    if (restoreConfirmCode !== 'RESTORE') return;
                    setRestoring(true);
                    setMessage(null);
                    try {
                      const result = await restoreFromBackup(restoreData, restoreConfirmCode);
                      setRestoreResult({ success: true, stats: result.stats });
                      setRestoreData(null);
                      setRestoreFile(null);
                      setRestoreConfirmCode('');
                      loadStats();
                    } catch (error) {
                      setRestoreResult({ success: false, error: error.message });
                    } finally {
                      setRestoring(false);
                    }
                  }}
                  disabled={restoring || restoreConfirmCode !== 'RESTORE'}
                >
                  {restoring ? 'Restoring...' : 'Confirm Restore'}
                </button>
                <button
                  class="btn btn-secondary"
                  onClick={() => {
                    setRestoreData(null);
                    setRestoreFile(null);
                    setRestoreConfirmCode('');
                    setRestoreResult(null);
                  }}
                  disabled={restoring}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Integrations Tab
// =============================================================================

function IntegrationsTab() {
  const { hasFeature } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showLogs, setShowLogs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showBambooHRWizard, setShowBambooHRWizard] = useState(false);

  useEffect(() => {
    loadIntegrations();
    // Check URL for OAuth callback results
    const params = new URLSearchParams(window.location.search);
    const providers = ['google_calendar', 'outlook'];
    for (const provider of providers) {
      if (params.get(provider) === 'connected') {
        setSuccess(`${provider.replace('_', ' ')} connected successfully!`);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      } else if (params.get(provider) === 'error') {
        setError(`Failed to connect ${provider.replace('_', ' ')}`);
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  async function loadIntegrations() {
    try {
      setLoading(true);
      const data = await getIntegrations();
      setIntegrations(data.integrations || []);
    } catch (err) {
      console.error('Failed to load integrations:', err);
      setError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  async function handleConnect(provider, authType) {
    // Special handling for BambooHR - show wizard
    if (provider === 'bamboohr') {
      setShowBambooHRWizard(true);
      return;
    }

    try {
      setConnecting(provider);
      setError(null);
      const result = await connectIntegration(provider);

      if (result.authUrl) {
        // OAuth flow - redirect to auth URL
        window.location.href = result.authUrl;
      } else if (result.success) {
        // API key flow - already connected
        setSuccess(result.message || 'Connected successfully');
        await loadIntegrations();
      }
    } catch (err) {
      console.error('Failed to connect:', err);
      setError(err.message || 'Failed to connect integration');
    } finally {
      setConnecting(null);
    }
  }

  async function handleDisconnect(provider) {
    if (!confirm('Are you sure you want to disconnect this integration?')) return;

    try {
      setConnecting(provider);
      setError(null);
      await disconnectIntegration(provider);
      setSuccess('Integration disconnected');
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError(err.message || 'Failed to disconnect integration');
    } finally {
      setConnecting(null);
    }
  }

  async function handleSync(provider) {
    try {
      setSyncing(provider);
      setError(null);
      const result = await syncIntegration(provider);
      if (result.success) {
        setSuccess(`Synced ${result.recordsSynced} records`);
      } else {
        setError(result.errors?.join(', ') || 'Sync failed');
      }
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to sync:', err);
      setError(err.message || 'Failed to sync integration');
    } finally {
      setSyncing(null);
    }
  }

  async function handleShowLogs(provider) {
    try {
      setShowLogs(provider);
      setLogsLoading(true);
      const data = await getIntegrationLogs(provider, { limit: 10 });
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }

  // Group integrations by type
  const calendarIntegrations = integrations.filter(i => i.type === 'calendar');
  const hrisIntegrations = integrations.filter(i => i.type === 'hris');
  const payrollIntegrations = integrations.filter(i => i.type === 'payroll');

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style={{ minHeight: '200px' }}>
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      {error && (
        <div class="alert alert-danger" style={{ marginBottom: '1rem' }}>
          {error}
          <button class="alert-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div class="alert alert-success" style={{ marginBottom: '1rem' }}>
          {success}
          <button class="alert-close" onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      {/* Calendar Integrations */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>📅 Calendar Integrations</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Sync approved leave to your team's calendars automatically
          </p>
        </div>
        <div class="card-body">
          <div class="integrations-grid">
            {calendarIntegrations.map(integration => (
              <IntegrationCard
                key={integration.provider}
                integration={integration}
                connecting={connecting === integration.provider}
                syncing={syncing === integration.provider}
                onConnect={() => handleConnect(integration.provider, integration.authType)}
                onDisconnect={() => handleDisconnect(integration.provider)}
                onSync={() => handleSync(integration.provider)}
                onShowLogs={() => handleShowLogs(integration.provider)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* HRIS Integrations */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>👥 HR System Integrations</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Sync employee data with your HR information system
          </p>
        </div>
        <div class="card-body">
          <div class="integrations-grid">
            {hrisIntegrations.map(integration => (
              <IntegrationCard
                key={integration.provider}
                integration={integration}
                connecting={connecting === integration.provider}
                syncing={syncing === integration.provider}
                onConnect={() => handleConnect(integration.provider, integration.authType)}
                onDisconnect={() => handleDisconnect(integration.provider)}
                onSync={() => handleSync(integration.provider)}
                onShowLogs={() => handleShowLogs(integration.provider)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Payroll Integrations */}
      <div class="card">
        <div class="card-header">
          <h3>💰 Payroll Integrations</h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Export leave data to your payroll system
          </p>
        </div>
        <div class="card-body">
          <div class="integrations-grid">
            {payrollIntegrations.map(integration => (
              <IntegrationCard
                key={integration.provider}
                integration={integration}
                connecting={connecting === integration.provider}
                syncing={syncing === integration.provider}
                onConnect={() => handleConnect(integration.provider, integration.authType)}
                onDisconnect={() => handleDisconnect(integration.provider)}
                onSync={() => handleSync(integration.provider)}
                onShowLogs={() => handleShowLogs(integration.provider)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Sync Logs Modal */}
      {showLogs && (
        <div class="modal-overlay" onClick={() => setShowLogs(null)}>
          <div class="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div class="modal-header">
              <h2>Sync History</h2>
              <button class="modal-close" onClick={() => setShowLogs(null)}>&times;</button>
            </div>
            <div class="modal-body">
              {logsLoading ? (
                <div class="loading-screen" style={{ minHeight: '100px' }}>
                  <div class="spinner" />
                </div>
              ) : logs.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                  No sync history yet
                </p>
              ) : (
                <table class="data-table" style={{ fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.startedAt).toLocaleString()}</td>
                        <td style={{ textTransform: 'capitalize' }}>{log.syncType}</td>
                        <td>
                          <span class={`status-badge ${log.status === 'success' ? 'approved' : log.status === 'failed' ? 'rejected' : 'pending'}`}>
                            {log.status}
                          </span>
                        </td>
                        <td>{log.recordsSynced}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BambooHR Wizard */}
      {showBambooHRWizard && (
        <BambooHRWizard
          onClose={() => setShowBambooHRWizard(false)}
          onComplete={() => {
            setShowBambooHRWizard(false);
            setSuccess('BambooHR data imported successfully');
            loadIntegrations();
          }}
          existingConfig={integrations.find(i => i.provider === 'bamboohr' && i.isConnected)}
        />
      )}

      <style>{`
        .integrations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .integration-card {
          border: 1px solid var(--color-border, #e5e7eb);
          border-radius: 12px;
          padding: 1.25rem;
          background: var(--color-bg, #fff);
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .integration-card:hover {
          border-color: var(--color-primary, #4F46E5);
        }

        .integration-card.connected {
          border-color: var(--color-success, #10b981);
          background: linear-gradient(to bottom right, rgba(16, 185, 129, 0.02), transparent);
        }

        .integration-card.locked {
          opacity: 0.6;
          background: var(--color-bg-secondary, #f9fafb);
        }

        .integration-header {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .integration-icon {
          font-size: 2rem;
          line-height: 1;
        }

        .integration-info {
          flex: 1;
          min-width: 0;
        }

        .integration-name {
          font-weight: 600;
          font-size: 1rem;
          color: var(--color-text, #111827);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .integration-status {
          font-size: 0.75rem;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          font-weight: 500;
        }

        .integration-status.connected {
          background: var(--color-success-bg, #d1fae5);
          color: var(--color-success, #059669);
        }

        .integration-status.error {
          background: var(--color-danger-bg, #fee2e2);
          color: var(--color-danger, #dc2626);
        }

        .integration-description {
          font-size: 0.8125rem;
          color: var(--color-text-secondary, #6b7280);
          margin-top: 0.25rem;
        }

        .integration-meta {
          font-size: 0.75rem;
          color: var(--color-text-tertiary, #9ca3af);
          margin-top: 0.5rem;
        }

        .integration-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }

        .integration-actions .btn {
          flex: 1;
          min-width: 0;
          padding: 0.5rem 0.75rem;
          font-size: 0.8125rem;
        }

        .upgrade-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          background: linear-gradient(135deg, #4F46E5, #7C3AED);
          color: white;
          border-radius: 6px;
          font-weight: 500;
        }

        .alert {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .alert-success {
          background: var(--color-success-bg, #d1fae5);
          color: var(--color-success-dark, #065f46);
          border: 1px solid var(--color-success-light, #a7f3d0);
        }

        .alert-danger {
          background: var(--color-danger-bg, #fee2e2);
          color: var(--color-danger-dark, #991b1b);
          border: 1px solid var(--color-danger-light, #fecaca);
        }

        .alert-close {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          opacity: 0.7;
          padding: 0 0.25rem;
        }

        .alert-close:hover {
          opacity: 1;
        }
      `}</style>
    </div>
  );
}

function IntegrationCard({ integration, connecting, syncing, onConnect, onDisconnect, onSync, onShowLogs }) {
  const { hasFeature } = useAuth();
  const hasAccess = integration.hasAccess;
  const isConnected = integration.isConnected;

  return (
    <div class={`integration-card ${isConnected ? 'connected' : ''} ${!hasAccess ? 'locked' : ''}`}>
      <div class="integration-header">
        <span class="integration-icon">{integration.icon}</span>
        <div class="integration-info">
          <div class="integration-name">
            {integration.name}
            {isConnected && (
              <span class="integration-status connected">Connected</span>
            )}
            {integration.status === 'error' && (
              <span class="integration-status error">Error</span>
            )}
          </div>
          <p class="integration-description">{integration.description}</p>
          {isConnected && integration.lastSyncAt && (
            <p class="integration-meta">
              Last synced: {new Date(integration.lastSyncAt).toLocaleString()}
            </p>
          )}
          {integration.lastError && (
            <p class="integration-meta" style={{ color: 'var(--color-danger)' }}>
              {integration.lastError}
            </p>
          )}
        </div>
      </div>

      <div class="integration-actions">
        {!hasAccess ? (
          <a href="/upgrade" class="btn btn-primary" style={{ textDecoration: 'none' }}>
            <span class="upgrade-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
              Upgrade
            </span>
          </a>
        ) : isConnected ? (
          <>
            <button
              class="btn btn-secondary"
              onClick={onSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              class="btn btn-secondary"
              onClick={onShowLogs}
            >
              History
            </button>
            <button
              class="btn btn-danger-outline"
              onClick={onDisconnect}
              disabled={connecting}
            >
              Disconnect
            </button>
          </>
        ) : (
          <button
            class="btn btn-primary"
            onClick={onConnect}
            disabled={connecting}
            style={{ width: '100%' }}
          >
            {connecting ? 'Connecting...' : `Connect ${integration.name}`}
          </button>
        )}
      </div>
    </div>
  );
}
